import React, { useState, useEffect, useRef, useMemo, memo, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Coin, Heart, Heartbeat, CloudLightning, Briefcase, Plant, 
  PaperPlaneTilt, SpinnerGap, Eye, ArrowLeft, EnvelopeSimple,
  Sparkle, MoonStars, Cards, Star, Hash, CalendarBlank,
  User, SignIn, SignOut, CurrencyCircleDollar,
  Gear, Copy, Check, ChartBar, CreditCard, Crown,
  EnvelopeOpen, SunDim, MusicNote, UserCircle, Gift, FileText, Globe, ShareNetwork,
  ArrowsClockwise, PencilSimple, Fire,
  MapPin, CurrencyDollar, Users, StarFour, Clock, Package,
  ChatCircleDots, ArrowRight
} from "@phosphor-icons/react";
import { useLanguage, LANGUAGE_NAMES, usePageTitle } from "./i18n";
import { useTheme } from "./theme";
import AdminPanel from "./AdminPanel";
import { LegalPage, Footer, CookieConsent, DivinationDisclaimer } from "./LegalPages";
import { MAJOR_ARCANA_IMAGES, CARD_NAME_TO_NUMBER, SUIT_STYLES } from "./tarotDesigns";
import { MINOR_ARCANA } from "./minorArcana";
import { TAROT_QUESTIONS, TAROT_CATEGORIES, getRandomQuestions } from "./tarotQuestions";
import TarotCardPicker from "./TarotCardPicker";
import AskezaChat from "./AskezaChat";
import GuidePage from "./GuidePage";
import { VoiceRecordButton, TTSPlayButton } from "./VoiceFeatures";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Build Minor Arcana lookup by name_ru and name for quick card image matching
const MINOR_ARCANA_LOOKUP = {};
Object.values(MINOR_ARCANA).forEach(card => {
  if (card.name_ru) MINOR_ARCANA_LOOKUP[card.name_ru] = card.image;
  if (card.name) MINOR_ARCANA_LOOKUP[card.name] = card.image;
});

// Mario-style Coin Component
const MarioCoin = ({ size = 16, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    className={`inline-block ${className}`}
    style={{ verticalAlign: 'middle' }}
  >
    <circle cx="12" cy="12" r="10" fill="#FFD700" stroke="#DAA520" strokeWidth="2"/>
    <circle cx="12" cy="12" r="7" fill="none" stroke="#FFA500" strokeWidth="1.5" opacity="0.6"/>
    <text x="12" y="16" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#B8860B">✦</text>
  </svg>
);

// Event tracking for analytics
const trackEvent = (event, userId, data) => {
  axios.post(`${API}/events/track`, {
    event, user_id: userId, data
  }).catch(() => {}); // Fire and forget
};

// Local storage keys
const USER_STORAGE_KEY = 'void_oracle_user';
const TOKEN_STORAGE_KEY = 'void_oracle_token';

// Helper functions for auth
const saveUser = (user, token) => {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
};

const loadUser = () => {
  try {
    const user = localStorage.getItem(USER_STORAGE_KEY);
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    return user ? { user: JSON.parse(user), token } : null;
  } catch {
    return null;
  }
};

const clearUser = () => {
  localStorage.removeItem(USER_STORAGE_KEY);
  localStorage.removeItem(TOKEN_STORAGE_KEY);
};

// Icon mapping for topics
const ICON_MAP = {
  Coin: Coin,
  Heart: Heart,
  Heartbeat: Heartbeat,
  CloudLightning: CloudLightning,
  Briefcase: Briefcase,
  Plant: Plant,
  Fire: Fire,
};

// Icon mapping for divination
const DIVINATION_ICON_MAP = {
  Cards: Cards,
  Star: Star,
  Hash: Hash,
};

// Background particles component
// AnimatedBackground is used instead - see below

// Topic Card Component
const TopicCard = ({ topic, onClick, index }) => {
  const { t } = useLanguage();
  const IconComponent = ICON_MAP[topic.icon] || Sparkle;
  const topicName = t(`topics.${topic.id}`) || topic.name_ru;
  const topicDesc = t(`topics.${topic.id}Desc`) || topic.description;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      onClick={onClick}
      data-testid={`topic-card-${topic.id}`}
      className="group relative cursor-pointer"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-ethereal/20 to-starlight/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500" />
      <div className="relative bg-[var(--card-bg)] backdrop-blur-xl border border-[var(--glass-border)] rounded-2xl p-8 
                      hover:border-starlight/30 hover:bg-[var(--overlay-bg)] 
                      transition-all duration-500 h-full
                      hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(212,175,55,0.2)]">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-starlight/10 border border-starlight/30 
                          flex items-center justify-center
                          group-hover:bg-starlight/20 group-hover:animate-glow-pulse transition-all duration-300">
            <IconComponent 
              size={32} 
              weight="duotone" 
              className="text-starlight group-hover:drop-shadow-[0_0_8px_rgba(212,175,55,0.5)]" 
            />
          </div>
          <h3 className="font-heading text-xl tracking-widest uppercase text-starlight">
            {topicName}
          </h3>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {topicDesc}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Divination Card Component
const DivinationCard = ({ method, onClick, index }) => {
  const { t } = useLanguage();
  const IconComponent = DIVINATION_ICON_MAP[method.icon] || Sparkle;
  const methodName = t(`divination.${method.id}`) || method.name_ru;
  const methodDesc = t(`divination.${method.id}Desc`) || method.description;
  
  // Per-method accent colors
  const accents = {
    tarot: { border: 'border-purple-500/40', hoverBorder: 'hover:border-purple-400', glow: 'rgba(168,85,247,0.4)', bg: 'bg-purple-500/10', hoverBg: 'group-hover:bg-purple-500/20', text: 'text-purple-400', iconDrop: 'drop-shadow-[0_0_10px_rgba(168,85,247,0.6)]' },
    horoscope: { border: 'border-amber-500/40', hoverBorder: 'hover:border-amber-400', glow: 'rgba(245,158,11,0.4)', bg: 'bg-amber-500/10', hoverBg: 'group-hover:bg-amber-500/20', text: 'text-amber-400', iconDrop: 'drop-shadow-[0_0_10px_rgba(245,158,11,0.6)]' },
    numerology: { border: 'border-cyan-500/40', hoverBorder: 'hover:border-cyan-400', glow: 'rgba(6,182,212,0.4)', bg: 'bg-cyan-500/10', hoverBg: 'group-hover:bg-cyan-500/20', text: 'text-cyan-400', iconDrop: 'drop-shadow-[0_0_10px_rgba(6,182,212,0.6)]' },
  };
  const a = accents[method.id] || accents.tarot;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
      onClick={onClick}
      data-testid={`divination-card-${method.id}`}
      className="group relative cursor-pointer"
    >
      <div className={`absolute inset-0 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500`}
           style={{ background: `radial-gradient(circle, ${a.glow} 0%, transparent 70%)` }} />
      <div className={`relative bg-[var(--card-bg)] backdrop-blur-xl border ${a.border} rounded-2xl p-7 
                      ${a.hoverBorder} hover:bg-[var(--overlay-bg)] 
                      transition-all duration-500 h-full
                      hover:-translate-y-2`}
           style={{ '--card-glow': a.glow }}
      >
        <div className="flex flex-col items-center text-center space-y-4">
          <div className={`w-16 h-16 rounded-full ${a.bg} ${a.hoverBg} border ${a.border}
                          flex items-center justify-center transition-all duration-300`}>
            <IconComponent 
              size={32} 
              weight="duotone" 
              className={`${a.text} group-hover:${a.iconDrop} transition-all duration-300 group-hover:scale-110`} 
            />
          </div>
          <h3 className={`font-heading text-lg tracking-widest uppercase ${a.text}`}>
            {methodName}
          </h3>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {methodDesc}
          </p>
          <div className={`mt-1 px-4 py-1.5 rounded-full border ${a.border} ${a.bg} opacity-0 group-hover:opacity-100 
                          transition-all duration-300 transform translate-y-2 group-hover:translate-y-0`}>
            <span className={`${a.text} text-xs font-heading tracking-widest uppercase`}>
              {t('divination.begin') || 'Begin'}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Animated Stars Background
// Memoized animated background - prevents re-render on input changes
const AnimatedBackground = memo(() => {
  const themeCtx = useTheme();
  const isLight = themeCtx?.theme === 'light';
  
  // Dark theme particles
  const particles = useMemo(() => 
    [...Array(50)].map((_, i) => ({
      id: i,
      width: Math.random() * 3 + 1,
      height: Math.random() * 3 + 1,
      left: Math.random() * 100,
      top: Math.random() * 100,
      darkColor: i % 3 === 0 ? '#D4AF37' : i % 3 === 1 ? '#7B2CBF' : '#4CC9F0',
      opacity: Math.random() * 0.5 + 0.2,
      duration: 8 + Math.random() * 12,
      delay: Math.random() * 5,
    })), []
  );

  // Light theme particles — brighter, with glow
  const lightParticles = useMemo(() => 
    [...Array(60)].map((_, i) => ({
      id: i,
      size: Math.random() * 5 + 2,
      left: Math.random() * 100,
      top: Math.random() * 100,
      color: i % 4 === 0 ? '#D4AF37' : i % 4 === 1 ? '#8B5CF6' : i % 4 === 2 ? '#C084FC' : '#F59E0B',
      opacity: Math.random() * 0.8 + 0.3,
      duration: 6 + Math.random() * 10,
      delay: Math.random() * 6,
    })), []
  );

  const shootingStars = useMemo(() => 
    [...Array(3)].map((_, i) => ({
      id: i,
      left: 20 + i * 30,
      top: 10 + i * 15,
      duration: 15 + i * 5,
      delay: i * 8,
    })), []
  );

  // Light theme: aurora ribbons — more vibrant
  const auroraRibbons = useMemo(() => 
    [...Array(5)].map((_, i) => ({
      id: i,
      top: 10 + i * 18,
      height: 100 + Math.random() * 140,
      color1: i % 3 === 0 ? 'rgba(196,132,252,0.2)' : i % 3 === 1 ? 'rgba(212,175,55,0.18)' : 'rgba(139,92,246,0.15)',
      color2: i % 3 === 0 ? 'rgba(139,92,246,0.1)' : i % 3 === 1 ? 'rgba(245,158,11,0.08)' : 'rgba(196,132,252,0.07)',
      duration: 20 + Math.random() * 15,
      delay: i * 3,
    })), []
  );

  // Light theme: glowing orbs (more vibrant)
  const lightOrbs = useMemo(() => 
    [...Array(5)].map((_, i) => ({
      id: i,
      size: 180 + Math.random() * 250,
      left: Math.random() * 100,
      top: Math.random() * 100,
      color: i % 3 === 0 ? 'rgba(212, 175, 55, 0.18)' : i % 3 === 1 ? 'rgba(147, 51, 234, 0.14)' : 'rgba(196, 132, 252, 0.12)',
      duration: 18 + Math.random() * 12,
      delay: Math.random() * 8,
    })), []
  );

  // Light theme: shooting sparkles (horizontal light streaks)
  const lightSparks = useMemo(() => 
    [...Array(4)].map((_, i) => ({
      id: i,
      left: 10 + i * 25,
      top: 15 + i * 20,
      duration: 12 + i * 4,
      delay: i * 6,
    })), []
  );

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {isLight ? (
        <>
          {/* Light theme: Ethereal sky background with slow pan */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url('https://images.pexels.com/photos/11915890/pexels-photo-11915890.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'brightness(1.1) saturate(0.9)',
              animation: 'slowPan 120s ease-in-out infinite alternate',
            }}
          />
          {/* Soft overlay for readability */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.7) 30%, rgba(255,255,255,0.6) 100%)',
            }}
          />
          {/* Aurora ribbons — flowing color bands */}
          {auroraRibbons.map((r) => (
            <div
              key={`aurora-${r.id}`}
              className="absolute w-full"
              style={{
                top: `${r.top}%`,
                height: `${r.height}px`,
                background: `linear-gradient(90deg, transparent 0%, ${r.color1} 30%, ${r.color2} 70%, transparent 100%)`,
                animation: `auroraFlow ${r.duration}s ease-in-out infinite alternate`,
                animationDelay: `${r.delay}s`,
                filter: 'blur(40px)',
              }}
            />
          ))}
          {/* Pulsing glowing orbs */}
          {lightOrbs.map((orb) => (
            <div
              key={`orb-${orb.id}`}
              className="absolute rounded-full"
              style={{
                width: `${orb.size}px`,
                height: `${orb.size}px`,
                left: `${orb.left}%`,
                top: `${orb.top}%`,
                background: `radial-gradient(circle, ${orb.color} 0%, transparent 70%)`,
                animation: `floatOrbPulse ${orb.duration}s ease-in-out infinite`,
                animationDelay: `${orb.delay}s`,
                filter: 'blur(30px)',
              }}
            />
          ))}
          {/* Bright floating particles with glow */}
          {lightParticles.map((p) => (
            <div
              key={`lp-${p.id}`}
              className="absolute rounded-full"
              style={{
                width: `${p.size}px`,
                height: `${p.size}px`,
                left: `${p.left}%`,
                top: `${p.top}%`,
                background: p.color,
                opacity: p.opacity,
                animation: `floatStar ${p.duration}s ease-in-out infinite`,
                animationDelay: `${p.delay}s`,
                boxShadow: `0 0 ${p.size * 3}px ${p.size}px ${p.color}40`,
              }}
            />
          ))}
          {/* Shooting sparkles — golden light streaks */}
          {lightSparks.map((s) => (
            <div
              key={`spark-${s.id}`}
              className="absolute rounded-full"
              style={{
                width: '2px',
                height: '2px',
                left: `${s.left}%`,
                top: `${s.top}%`,
                background: '#D4AF37',
                animation: `lightSparkle ${s.duration}s linear infinite`,
                animationDelay: `${s.delay}s`,
                boxShadow: '0 0 8px 3px rgba(212,175,55,0.5), -15px 0 8px rgba(212,175,55,0.3), -30px 0 4px rgba(212,175,55,0.15)',
              }}
            />
          ))}
          {/* Breathing shimmer overlay */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.06) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(147,51,234,0.05) 0%, transparent 50%)',
              animation: 'breatheShimmer 8s ease-in-out infinite',
            }}
          />
        </>
      ) : (
        <>
          {/* Dark theme: Nebula background */}
          <div 
            className="absolute inset-0 opacity-60"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1640297013018-adaae8fd5536?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxteXN0aWNhbCUyMGRhcmslMjBibHVlJTIwbmVidWxhJTIwc3BhY2UlMjBiYWNrZ3JvdW5kJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzcxNzYwMzgwfDA&ixlib=rb-4.1.0&q=85')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              animation: 'slowPan 120s ease-in-out infinite alternate',
            }}
          />
          <div className="absolute inset-0 bg-[var(--overlay-bg)]" />
        </>
      )}
      
      {/* Dark theme floating particles */}
      {!isLight && particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: `${p.width}px`,
            height: `${p.height}px`,
            left: `${p.left}%`,
            top: `${p.top}%`,
            background: p.darkColor,
            opacity: p.opacity,
            animation: `floatStar ${p.duration}s ease-in-out infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
      
      {/* Shooting stars (dark theme only) */}
      {!isLight && shootingStars.map((s) => (
        <div
          key={`shooting-${s.id}`}
          className="absolute w-1 h-1 bg-white rounded-full"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            animation: `shootingStar ${s.duration}s linear infinite`,
            animationDelay: `${s.delay}s`,
            boxShadow: '0 0 6px 2px rgba(255,255,255,0.4), -20px 0 10px rgba(255,255,255,0.2), -40px 0 6px rgba(255,255,255,0.1)',
          }}
        />
      ))}
    </div>
  );
});

// Auth Modal Component
const AuthModal = ({ onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [mode, setMode] = useState('login'); // 'login', 'register', 'verify'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifyUserId, setVerifyUserId] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyUserData, setVerifyUserData] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);

  // Get referral code from sessionStorage
  const getReferralCode = () => sessionStorage.getItem('referral_code') || '';
  const [referralCode, setReferralCode] = useState(getReferralCode());

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleResendCode = async () => {
    if (resendTimer > 0 || !verifyUserId) return;
    try {
      // In strict_mode, challenge_token is required for resend
      let challengeToken = null;
      try {
        const statusResp = await axios.get(`${API}/auth/security-status`);
        if (statusResp.data.strict_mode) {
          const challengeResp = await axios.get(`${API}/auth/register-challenge`);
          challengeToken = challengeResp.data.challenge_token;
        }
      } catch {}
      await axios.post(`${API}/auth/resend-verification`, { 
        user_id: verifyUserId,
        challenge_token: challengeToken 
      });
      setResendTimer(60);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error');
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      // SECURITY: Fetch anti-bot challenge token before verify
      let challengeToken = null;
      try {
        const challengeResp = await axios.get(`${API}/auth/register-challenge`);
        challengeToken = challengeResp.data.challenge_token;
      } catch {}
      
      const response = await axios.post(`${API}/auth/verify-email`, {
        user_id: verifyUserId,
        code: verifyCode,
        challenge_token: challengeToken || ''
      });
      if (response.data.success) {
        // SECURITY: Only use session token (never fall back to user_id)
        saveUser(response.data.user, response.data.token);
        onSuccess(response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (mode === 'register') {
        const refCode = referralCode || getReferralCode();
        
        // Fetch anti-bot challenge token before registration
        let challengeToken = null;
        try {
          const challengeResp = await axios.get(`${API}/auth/register-challenge`);
          challengeToken = challengeResp.data.challenge_token;
        } catch {}
        
        const response = await axios.post(`${API}/auth/register`, {
          email,
          password,
          name: name || undefined,
          referral_code: refCode || undefined,
          challenge_token: challengeToken
        });
        // Clear referral code after successful registration
        sessionStorage.removeItem('referral_code');
        
        if (response.data.requires_verification) {
          // SECURITY: Server now returns only user_id (no token, no user object)
          setVerifyUserId(response.data.user_id || response.data.user?.id);
          setVerifyUserData(null);
          setMode('verify');
          setResendTimer(60);
        } else {
          saveUser(response.data.user, response.data.token);
          onSuccess(response.data.user);
        }
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          email,
          password
        });
        
        // Check if user needs verification
        if (response.data.requires_verification || (response.data.user && !response.data.user.email_verified && response.data.user.auth_provider === 'email')) {
          // Trigger verification resend
          const userId = response.data.user?.id || response.data.user_id;
          try {
            let ct = null;
            try {
              const ss = await axios.get(`${API}/auth/security-status`);
              if (ss.data.strict_mode) {
                const cr = await axios.get(`${API}/auth/register-challenge`);
                ct = cr.data.challenge_token;
              }
            } catch {}
            await axios.post(`${API}/auth/resend-verification`, { user_id: userId, challenge_token: ct });
          } catch {}
          // Store user.id for the verify-email API call
          setVerifyUserId(userId);
          setVerifyUserData(null);
          setMode('verify');
          setResendTimer(60);
        } else {
          saveUser(response.data.user, response.data.token);
          onSuccess(response.data.user);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Verification screen
  if (mode === 'verify') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[999999] flex items-center justify-center p-6"
      >
        <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative bg-void-blue/95 backdrop-blur-xl border border-starlight/30 rounded-2xl p-8 max-w-md w-full shadow-[0_0_60px_rgba(212,175,55,0.2)]"
          data-testid="verify-modal"
        >
          <div className="text-center mb-6">
            <EnvelopeSimple size={48} weight="duotone" className="text-starlight mx-auto mb-3" />
            <h2 className="font-heading text-2xl tracking-widest uppercase text-starlight">
              {t('auth.verifyTitle') || 'Подтверждение'}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm">
              {(t('auth.verifySubtitle') || 'Код отправлен на') + ' ' + (email || '')}
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-4">
            <div className="flex justify-center">
              <input
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="w-48 bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-4
                           text-foreground text-center text-2xl tracking-[0.5em] font-mono
                           focus:border-starlight/60 focus:outline-none
                           placeholder:text-muted-foreground"
                data-testid="verify-code-input"
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || verifyCode.length !== 6}
              className="w-full bg-starlight/10 border border-starlight/50 text-starlight
                         py-4 rounded-xl font-heading tracking-widest uppercase
                         hover:bg-starlight/20 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-all duration-300 active:scale-95"
              data-testid="verify-submit-btn"
            >
              {isLoading ? (
                <SpinnerGap size={24} className="animate-spin mx-auto" />
              ) : (
                t('auth.verifyButton') || 'Подтвердить'
              )}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={handleResendCode}
              disabled={resendTimer > 0}
              className="text-muted-foreground hover:text-starlight transition-colors text-sm disabled:opacity-40"
              data-testid="resend-code-btn"
            >
              {resendTimer > 0 
                ? `${t('auth.resendIn') || 'Отправить повторно через'} ${resendTimer}с`
                : (t('auth.resendCode') || 'Отправить код повторно')}
            </button>
          </div>

          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          >
            ✕
          </button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999999] flex items-center justify-center p-6"
    >
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-void-blue/95 backdrop-blur-xl border border-starlight/30 rounded-2xl p-8 max-w-md w-full shadow-[0_0_60px_rgba(212,175,55,0.2)]"
        data-testid="auth-modal"
      >
        <div className="text-center mb-6">
          <User size={48} weight="duotone" className="text-starlight mx-auto mb-3" />
          <h2 className="font-heading text-2xl tracking-widest uppercase text-starlight">
            {mode === 'login' ? t('auth.loginTitle') : t('auth.registerTitle')}
          </h2>
          <p className="text-muted-foreground mt-2 text-sm">
            {mode === 'login' 
              ? t('auth.loginSubtitle')
              : t('auth.registerSubtitle')}
          </p>
        </div>

        {/* Google Login Button */}
        <button
          onClick={handleGoogleLogin}
          className="w-full bg-white/10 border border-white/20 text-foreground
                     py-3 rounded-xl flex items-center justify-center gap-3
                     hover:bg-white/20 transition-all duration-300 mb-4"
          data-testid="google-login-btn"
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span>{t('auth.googleLogin')}</span>
        </button>

        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-white/20" />
          <span className="text-muted-foreground text-sm">{t('auth.or')}</span>
          <div className="flex-1 h-px bg-white/20" />
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('auth.name')}
              className="w-full bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm
                         text-foreground placeholder:text-muted-foreground
                         focus:border-starlight/60 focus:outline-none transition-colors"
              data-testid="auth-name-input"
            />
          )}
          
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.email')}
            required
            className="w-full bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm
                       text-foreground placeholder:text-muted-foreground
                       focus:border-starlight/60 focus:outline-none transition-colors"
            data-testid="auth-email-input"
          />
          
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.password')}
            required
            minLength={6}
            className="w-full bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm
                       text-foreground placeholder:text-muted-foreground
                       focus:border-starlight/60 focus:outline-none transition-colors"
            data-testid="auth-password-input"
          />
          
          {mode === 'register' && (
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder={t('auth.referralCode')}
              className="w-full bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm
                         text-foreground placeholder:text-muted-foreground
                         focus:border-starlight/60 focus:outline-none transition-colors"
              data-testid="auth-referral-input"
            />
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-starlight/10 border border-starlight/50 text-starlight
                       py-4 rounded-xl font-heading tracking-widest uppercase
                       hover:bg-starlight/20 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-300 active:scale-95"
            data-testid="auth-submit-btn"
          >
            {isLoading ? (
              <SpinnerGap size={24} className="animate-spin mx-auto" />
            ) : (
              mode === 'login' ? t('auth.loginButton') : t('auth.registerButton')
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="text-muted-foreground hover:text-starlight transition-colors text-sm"
          >
            {mode === 'login' 
              ? t('auth.switchToRegister')
              : t('auth.switchToLogin')}
          </button>
        </div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          ✕
        </button>
      </motion.div>
    </motion.div>
  );
};

// Language Switcher Component
const LanguageSwitcher = ({ compact = false }) => {
  const { language, setLanguage, SUPPORTED_LANGUAGES } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 bg-[var(--card-bg)] backdrop-blur-xl border border-ethereal/20 
                   rounded-full hover:border-ethereal/40 transition-all
                   ${compact ? 'p-2' : 'px-3 py-2'}`}
        data-testid="language-switcher"
      >
        <Globe size={18} className="text-ethereal" />
        {!compact && (
          <span className="text-sm text-foreground/80">{LANGUAGE_NAMES[language]?.flag}</span>
        )}
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute right-0 top-full mt-2 z-50 bg-black/90 backdrop-blur-xl 
                       border border-ethereal/20 rounded-xl overflow-hidden min-w-[140px]"
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang}
                onClick={() => {
                  setLanguage(lang);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2.5 text-left flex items-center gap-3 
                           hover:bg-ethereal/10 transition-all text-sm
                           ${language === lang ? 'bg-ethereal/20 text-ethereal' : 'text-foreground/80'}`}
                data-testid={`lang-${lang}`}
              >
                <span>{LANGUAGE_NAMES[lang]?.flag}</span>
                <span>{LANGUAGE_NAMES[lang]?.name}</span>
              </button>
            ))}
          </motion.div>
        </>
      )}
    </div>
  );
};

// User Header Component
const UserHeader = ({ user, onLogout, onShowAuth, onShowProfile, onShowCreditShop }) => {
  const { t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  
  return (
    <div className="fixed top-4 right-4 z-50 flex items-center gap-3">
      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className="bg-[var(--card-bg)] backdrop-blur-xl border border-[var(--glass-border)] rounded-full p-2.5
                   hover:border-starlight/50 transition-all"
        data-testid="theme-toggle-btn"
      >
        {theme === 'dark' ? (
          <SunDim size={20} className="text-starlight" />
        ) : (
          <MoonStars size={20} className="text-ethereal" />
        )}
      </button>
      
      {/* Language Switcher */}
      <LanguageSwitcher compact />
      
      {user ? (
        <>
          <button
            onClick={() => onShowCreditShop()}
            className="bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/20 rounded-full px-4 py-2 flex items-center gap-2
                       hover:border-starlight/40 hover:bg-starlight/5 transition-all cursor-pointer"
            data-testid="header-credits-btn"
          >
            <MarioCoin size={20} />
            <span className="text-starlight font-medium">{user.total_credits}</span>
          </button>
          <button
            onClick={onShowProfile}
            className="bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/20 rounded-full p-1.5
                       hover:border-starlight/50 transition-all flex items-center gap-2 pr-3"
            data-testid="profile-btn"
          >
            {user.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-starlight/20 flex items-center justify-center text-starlight text-xs font-bold">
                {(user.name || user.email)?.[0]?.toUpperCase()}
              </div>
            )}
            <span className="text-sm text-foreground/80 max-w-[100px] truncate hidden md:block">
              {user.name || user.email?.split('@')[0]}
            </span>
          </button>
        </>
      ) : (
        <button
          onClick={onShowAuth}
          className="bg-starlight/10 backdrop-blur-xl border border-starlight/30 rounded-full px-4 py-2
                     flex items-center gap-2 text-starlight
                     hover:bg-starlight/20 transition-all"
          data-testid="login-btn"
        >
          <SignIn size={20} />
          <span className="text-sm font-medium">{t('nav.login')}</span>
        </button>
      )}
    </div>
  );
};

// Askeza (Self-Discipline) Section for Profile Page
const AskezaSection = ({ user }) => {
  const { t, language } = useLanguage();
  const [askezas, setAskezas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [catalogCategories, setCatalogCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [newAskeza, setNewAskeza] = useState({ askeza_type: 'restriction', description: '', goal: '', duration_days: 21 });
  const [creating, setCreating] = useState(false);
  const [healthWarning, setHealthWarning] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/askeza/user/${user.id}`)
      .then(res => setAskezas(res.data.askezas || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.id]);

  const loadCatalog = async () => {
    if (catalog.length > 0) { setShowCatalog(true); return; }
    setCatalogLoading(true);
    try {
      const res = await axios.get(`${API}/askeza/catalog?language=${language}`);
      setCatalog(res.data.catalog || []);
      setCatalogCategories(res.data.categories || {});
      setShowCatalog(true);
    } catch (e) { void 0; }
    finally { setCatalogLoading(false); }
  };

  const startFromCatalog = async (item) => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/askeza/from-catalog/${item.id}?user_id=${user.id}&goal=${encodeURIComponent(item.description)}`);
      if (res.data.health_warning) setHealthWarning(res.data.health_warning);
      setAskezas(prev => [res.data, ...prev]);
      setShowCatalog(false);
    } catch (e) { void 0; }
    finally { setCreating(false); }
  };

  const handleCreate = async () => {
    if (!newAskeza.description.trim() || !newAskeza.goal.trim()) return;
    setCreating(true);
    setHealthWarning(null);
    try {
      const res = await axios.post(`${API}/askeza/create`, {
        ...newAskeza,
        user_id: user.id,
        duration_days: newAskeza.duration_days || null
      });
      if (res.data.health_warning) {
        setHealthWarning(res.data.health_warning);
      }
      setAskezas(prev => [res.data, ...prev]);
      setShowCreate(false);
      setNewAskeza({ askeza_type: 'restriction', description: '', goal: '', duration_days: 21 });
    } catch (e) {
      void 0;
    } finally {
      setCreating(false);
    }
  };

  const handleCheckIn = async (id) => {
    try {
      await axios.post(`${API}/askeza/${id}/check-in?success=true`);
      setAskezas(prev => prev.map(a => a.id === id ? { ...a, check_ins_count: (a.check_ins_count || 0) + 1 } : a));
    } catch (e) {
      void 0;
    }
  };

  const handleComplete = async (id, status) => {
    try {
      await axios.post(`${API}/askeza/${id}/complete?status=${status}`);
      setAskezas(prev => prev.map(a => a.id === id ? { ...a, status } : a));
    } catch (e) {
      void 0;
    }
  };

  const activeAskezas = askezas.filter(a => a.status === 'active');
  const completedAskezas = askezas.filter(a => a.status !== 'active');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.32 }}
      className="bg-[var(--card-bg)] backdrop-blur-xl border border-orange-500/20 rounded-2xl p-6"
      data-testid="askeza-section"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Fire size={24} className="text-orange-400" />
          <h3 className="font-heading text-orange-400 tracking-widest uppercase text-sm">
            {t('askeza.title') || 'Аскезы'}
          </h3>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadCatalog}
            disabled={catalogLoading}
            className="text-xs bg-purple-500/10 border border-purple-500/30 text-purple-400 px-3 py-1.5 rounded-lg
                       hover:bg-purple-500/20 transition-all"
            data-testid="askeza-catalog-btn"
          >
            {catalogLoading ? <SpinnerGap size={14} className="animate-spin" /> : (t('askeza.browse') || 'Каталог')}
          </button>
          <button
            onClick={() => { setShowCreate(!showCreate); setShowCatalog(false); }}
            className="text-xs bg-orange-500/10 border border-orange-500/30 text-orange-400 px-3 py-1.5 rounded-lg
                       hover:bg-orange-500/20 transition-all"
            data-testid="askeza-create-btn"
          >
            {showCreate ? (t('askeza.cancel') || 'Отмена') : (t('askeza.new') || '+ Своя')}
          </button>
        </div>
      </div>

      {/* Health Warning */}
      {healthWarning && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4 text-sm"
          data-testid="askeza-health-warning"
        >
          {healthWarning}
        </motion.div>
      )}

      {/* Create Form */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-4"
          >
            <div className="space-y-3 p-4 bg-orange-500/5 border border-orange-500/15 rounded-xl">
              <div className="flex gap-2">
                <button
                  onClick={() => setNewAskeza(p => ({ ...p, askeza_type: 'restriction' }))}
                  className={`flex-1 py-2 rounded-lg text-xs font-heading tracking-wider uppercase transition-all
                    ${newAskeza.askeza_type === 'restriction'
                      ? 'bg-orange-500/20 border border-orange-500/40 text-orange-400'
                      : 'bg-white/5 border border-white/10 text-muted-foreground'}`}
                  data-testid="askeza-type-restriction"
                >
                  {t('askeza.restriction') || 'Ограничение'}
                </button>
                <button
                  onClick={() => setNewAskeza(p => ({ ...p, askeza_type: 'obligation' }))}
                  className={`flex-1 py-2 rounded-lg text-xs font-heading tracking-wider uppercase transition-all
                    ${newAskeza.askeza_type === 'obligation'
                      ? 'bg-orange-500/20 border border-orange-500/40 text-orange-400'
                      : 'bg-white/5 border border-white/10 text-muted-foreground'}`}
                  data-testid="askeza-type-obligation"
                >
                  {t('askeza.obligation') || 'Обязательство'}
                </button>
              </div>
              <input
                type="text"
                value={newAskeza.description}
                onChange={e => setNewAskeza(p => ({ ...p, description: e.target.value }))}
                placeholder={t('askeza.descPlaceholder') || 'Что именно? (напр. Отказ от сахара)'}
                className="w-full bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-foreground text-sm
                           placeholder:text-muted-foreground focus:border-starlight/60 focus:outline-none transition-colors"
                data-testid="askeza-description-input"
              />
              <input
                type="text"
                value={newAskeza.goal}
                onChange={e => setNewAskeza(p => ({ ...p, goal: e.target.value }))}
                placeholder={t('askeza.goalPlaceholder') || 'Цель: чего хочешь достичь?'}
                className="w-full bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-foreground text-sm
                           placeholder:text-muted-foreground focus:border-starlight/60 focus:outline-none transition-colors"
                data-testid="askeza-goal-input"
              />
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{t('askeza.duration') || 'Дней:'}</span>
                <input
                  type="number"
                  value={newAskeza.duration_days}
                  onChange={e => setNewAskeza(p => ({ ...p, duration_days: parseInt(e.target.value) || '' }))}
                  className="w-20 bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-2 py-2 text-foreground text-sm text-center
                             focus:border-starlight/60 focus:outline-none transition-colors"
                  data-testid="askeza-duration-input"
                  min="1"
                />
                <span className="text-xs text-muted-foreground/50">{t('askeza.daysOrEmpty') || '(пусто = бессрочно)'}</span>
              </div>
              <button
                onClick={handleCreate}
                disabled={creating || !newAskeza.description.trim() || !newAskeza.goal.trim()}
                className="w-full bg-orange-500/10 border border-orange-500/40 text-orange-400
                           py-2.5 rounded-lg font-heading tracking-wider text-xs uppercase
                           hover:bg-orange-500/20 transition-all disabled:opacity-40"
                data-testid="askeza-submit-btn"
              >
                {creating ? <SpinnerGap size={16} className="animate-spin mx-auto" /> : (t('askeza.start') || 'Начать аскезу')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Catalog Browser */}
      <AnimatePresence>
        {showCatalog && catalog.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-4"
          >
            <div className="p-4 bg-purple-500/5 border border-purple-500/15 rounded-xl">
              {/* Category pills */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`text-[10px] px-2.5 py-1 rounded-full transition-all ${
                    !selectedCategory ? 'bg-purple-500/20 border border-purple-500/40 text-purple-400' : 'bg-white/5 border border-white/10 text-muted-foreground'
                  }`}
                >
                  {language === 'ru' ? 'Все' : 'All'}
                </button>
                {Object.entries(catalogCategories).map(([key, cat]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key === selectedCategory ? null : key)}
                    className={`text-[10px] px-2.5 py-1 rounded-full transition-all ${
                      selectedCategory === key ? 'bg-purple-500/20 border border-purple-500/40 text-purple-400' : 'bg-white/5 border border-white/10 text-muted-foreground'
                    }`}
                  >
                    {language === 'ru' ? cat.name_ru : cat.name_en}
                  </button>
                ))}
              </div>

              {/* Catalog items */}
              <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1 scrollbar-thin">
                {catalog
                  .filter(item => !selectedCategory || item.category === selectedCategory)
                  .slice(0, 20)
                  .map(item => {
                    const catInfo = catalogCategories[item.category] || {};
                    return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-2.5 rounded-lg bg-white/3 border border-white/5 hover:border-purple-500/20 transition-all group"
                        data-testid={`catalog-item-${item.id}`}
                      >
                        <div className="flex-1 min-w-0 mr-3">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: `${catInfo.color || '#8b5cf6'}15`, color: catInfo.color || '#8b5cf6', border: `1px solid ${catInfo.color || '#8b5cf6'}30` }}>
                              {language === 'ru' ? catInfo.name_ru : catInfo.name_en}
                            </span>
                            <span className="text-[9px] text-muted-foreground/40">{item.duration_days}d</span>
                            {item.difficulty === 'hard' && <span className="text-[9px] text-red-400/60">!</span>}
                          </div>
                          <p className="text-foreground text-xs truncate">{item.name}</p>
                        </div>
                        <button
                          onClick={() => startFromCatalog(item)}
                          disabled={creating}
                          className="text-[10px] bg-orange-500/10 border border-orange-500/30 text-orange-400
                                     px-3 py-1 rounded-lg opacity-70 group-hover:opacity-100 transition-all
                                     hover:bg-orange-500/20 flex-shrink-0"
                          data-testid={`catalog-start-${item.id}`}
                        >
                          {creating ? '...' : (t('askeza.start') || 'Начать')}
                        </button>
                      </div>
                    );
                  })}
              </div>
              <button
                onClick={() => setShowCatalog(false)}
                className="mt-3 w-full text-muted-foreground/40 text-[10px] hover:text-muted-foreground transition-colors"
              >
                {t('askeza.cancel') || 'Закрыть'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Askezas */}
      {loading ? (
        <div className="flex justify-center py-4">
          <SpinnerGap size={24} className="text-orange-400 animate-spin" />
        </div>
      ) : activeAskezas.length === 0 && completedAskezas.length === 0 ? (
        <p className="text-center text-muted-foreground/50 text-sm py-4">
          {t('askeza.empty') || 'У вас пока нет аскез. Начните первую!'}
        </p>
      ) : (
        <div className="space-y-3">
          {activeAskezas.map(a => {
            const progress = a.duration_days ? Math.min((a.progress_days / a.duration_days) * 100, 100) : null;
            return (
              <div key={a.id} className="p-3 rounded-xl bg-orange-500/5 border border-orange-500/15" data-testid={`askeza-${a.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground text-sm font-medium truncate">{a.description}</p>
                    <p className="text-muted-foreground/60 text-[11px] mt-0.5">{a.goal}</p>
                  </div>
                  <span className="text-[10px] bg-orange-500/15 text-orange-400 px-2 py-0.5 rounded-full ml-2 flex-shrink-0">
                    {a.askeza_type === 'restriction' ? (t('askeza.restriction') || 'Огр.') : (t('askeza.obligation') || 'Обяз.')}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-orange-400 font-mono">
                    {t('askeza.day') || 'День'} {a.progress_days}
                  </span>
                  {a.duration_days && (
                    <>
                      <span className="text-xs text-muted-foreground/30">/</span>
                      <span className="text-xs text-muted-foreground/50">{a.duration_days}</span>
                    </>
                  )}
                </div>
                {progress !== null && (
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-2">
                    <motion.div
                      className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleCheckIn(a.id)}
                    className="flex-1 bg-green-500/10 border border-green-500/30 text-green-400
                               py-1.5 rounded-lg text-[11px] hover:bg-green-500/20 transition-all"
                    data-testid={`askeza-checkin-${a.id}`}
                  >
                    {t('askeza.checkIn') || 'Отметиться'} ({a.check_ins_count || 0})
                  </button>
                  <button
                    onClick={() => handleComplete(a.id, 'completed')}
                    className="bg-starlight/10 border border-starlight/30 text-starlight
                               px-3 py-1.5 rounded-lg text-[11px] hover:bg-starlight/20 transition-all"
                    data-testid={`askeza-complete-${a.id}`}
                  >
                    {t('askeza.complete') || 'Завершить'}
                  </button>
                  <button
                    onClick={() => handleComplete(a.id, 'abandoned')}
                    className="bg-red-500/5 border border-red-500/20 text-red-400/60
                               px-3 py-1.5 rounded-lg text-[11px] hover:bg-red-500/10 transition-all"
                    data-testid={`askeza-abandon-${a.id}`}
                  >
                    {t('askeza.abandon') || 'Прервать'}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Completed/Abandoned */}
          {completedAskezas.length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <p className="text-xs text-muted-foreground/40 mb-2">{t('askeza.history') || 'История'}</p>
              {completedAskezas.slice(0, 3).map(a => (
                <div key={a.id} className="flex items-center justify-between py-1.5 text-sm opacity-60">
                  <span className="text-foreground/60 text-xs truncate flex-1">{a.description}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    a.status === 'completed' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                  }`}>
                    {a.status === 'completed' ? (t('askeza.done') || 'Завершена') : (t('askeza.abandoned') || 'Прервана')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Profile Page Component
const ProfilePage = ({ user, onBack, onLogout, onUserUpdate }) => {
  const { t, language } = useLanguage();
  usePageTitle('profile');
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refCopied, setRefCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [horoscopeOrders, setHoroscopeOrders] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/user/${user.id}/stats`),
          axios.get(`${API}/horoscopes/orders/${user.id}`).catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data);
        setHoroscopeOrders(ordersRes.data || []);
        // Auto-detect user type if not set
        if (!user.user_type && statsRes.data.total_actions >= 3) {
          const detected = statsRes.data.tarot_readings + statsRes.data.horoscope_readings + statsRes.data.numerology_readings >= 3
            ? 'esoteric' : statsRes.data.oracle_messages >= 5 ? 'business' : null;
          if (detected) {
            const updated = await axios.put(`${API}/user/${user.id}/profile`, { user_type: detected });
            onUserUpdate(updated.data);
          }
        }
      } catch (e) {
        void 0;
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyReferral = () => {
    navigator.clipboard.writeText(user.referral_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePromoRedeem = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoResult(null);
    try {
      const res = await axios.post(`${API}/promo/redeem`, {
        user_id: user.id,
        code: promoCode.trim()
      });
      setPromoResult({ success: true, message: res.data.message });
      onUserUpdate(res.data.user);
      setPromoCode('');
    } catch (e) {
      setPromoResult({ success: false, message: e.response?.data?.detail || t('profile.activationError') });
    } finally {
      setPromoLoading(false);
    }
  };

  const userTypeLabels = {
    esoteric: { label: t('userTypes.esoteric'), desc: t('userTypes.esotericDesc') },
    business: { label: t('userTypes.business'), desc: t('userTypes.businessDesc') },
    discipline: { label: t('userTypes.discipline'), desc: t('userTypes.disciplineDesc') },
  };

  const typeInfo = userTypeLabels[user.user_type] || null;

  const handleBuyCredits = (pkg) => {
    const url = new URL(pkg.checkoutUrl);
    if (user?.email) {
      url.searchParams.set('checkout[email]', user.email);
    }
    window.open(url.toString(), '_blank');
  };

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      <AnimatedBackground />

      {/* Header */}
      <header className="sticky top-0 z-20 bg-[var(--card-bg)]/80 backdrop-blur-xl border-b border-[var(--glass-border)] px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="profile-back-btn"
          >
            <ArrowLeft size={20} />
            <span className="text-sm">{t('profile.back')}</span>
          </button>
          <span className="font-heading text-starlight tracking-widest uppercase text-sm">
            {t('profile.title')}
          </span>
          <div className="w-20" />
        </div>
      </header>

      <main className="relative z-10 max-w-2xl mx-auto px-6 py-8 space-y-6">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/20 rounded-2xl p-6"
          data-testid="profile-card"
        >
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-starlight/30" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-starlight/10 border-2 border-starlight/30 flex items-center justify-center">
                <span className="text-2xl font-heading text-starlight">
                  {(user.name || user.email)?.[0]?.toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-heading text-starlight tracking-wide truncate">
                {user.name || t('profile.seeker')}
              </h2>
              <p className="text-muted-foreground text-sm truncate">{user.email}</p>
              {typeInfo && (
                <span className="inline-block mt-1 text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full">
                  {typeInfo.label}
                </span>
              )}
            </div>
            <button
              onClick={onLogout}
              className="text-muted-foreground hover:text-red-400 transition-colors p-2"
              data-testid="profile-logout-btn"
            >
              <SignOut size={22} />
            </button>
          </div>
        </motion.div>

        {/* Balance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/20 rounded-2xl p-6"
          data-testid="balance-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <MarioCoin size={24} />
            <h3 className="font-heading text-starlight tracking-widest uppercase text-sm">{t('profile.balance')}</h3>
          </div>
          <div className="text-center py-4">
            <span className="text-5xl font-heading text-starlight">{user.total_credits}</span>
            <p className="text-muted-foreground text-sm mt-2">{t('profile.credits')}</p>
          </div>
        </motion.div>

        {/* Credit Packages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/20 rounded-2xl p-6"
          data-testid="credit-packages-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <CreditCard size={24} className="text-starlight" />
            <h3 className="font-heading text-starlight tracking-widest uppercase text-sm">{t('profile.buyCredits')}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {CREDIT_PACKAGES.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => handleBuyCredits(pkg)}
                className="bg-starlight/5 border border-starlight/20 rounded-xl p-4 text-center
                           hover:border-starlight/40 hover:bg-starlight/10 transition-all group
                           hover:scale-105 active:scale-95"
                data-testid={`buy-credits-${pkg.credits}`}
              >
                {pkg.bonus && (
                  <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full mb-2 inline-block">
                    {pkg.bonus}
                  </span>
                )}
                <span className="text-2xl font-heading text-starlight block">{pkg.credits}</span>
                <span className="text-xs text-muted-foreground block mt-1">{t('profile.credits')}</span>
                <span className="text-sm text-starlight font-medium block mt-2">{pkg.price}</span>
                <span className="text-[10px] text-muted-foreground/60 block">{pkg.perCredit}/{t('profile.credit') || 'cr'}</span>
              </button>
            ))}
          </div>
          <p className="text-center text-muted-foreground/60 text-xs mt-4">
            {t('profile.securePayment') || '🔒 Secure payment via LemonSqueezy'}
          </p>
        </motion.div>

        {/* Promo Code */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.17 }}
          className="bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/20 rounded-2xl p-6"
          data-testid="promo-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <Sparkle size={24} className="text-starlight" />
            <h3 className="font-heading text-starlight tracking-widest uppercase text-sm">{t('profile.promoCode')}</h3>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); }}
              placeholder={t('profile.enterPromo')}
              className="flex-1 bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-foreground text-sm
                         placeholder:text-muted-foreground focus:border-starlight/60 focus:outline-none transition-colors
                         font-mono tracking-wider uppercase"
              data-testid="promo-input"
              onKeyDown={(e) => e.key === 'Enter' && handlePromoRedeem()}
            />
            <button
              onClick={handlePromoRedeem}
              disabled={promoLoading || !promoCode.trim()}
              className="bg-starlight/10 border border-starlight/30 text-starlight px-4 py-2.5 rounded-lg
                         hover:bg-starlight/20 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="promo-submit-btn"
            >
              {promoLoading ? <SpinnerGap size={20} className="animate-spin" /> : t('profile.activate')}
            </button>
          </div>
          {promoResult && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-sm mt-3 text-center ${promoResult.success ? 'text-green-400' : 'text-red-400'}`}
              data-testid="promo-result"
            >
              {promoResult.message}
            </motion.p>
          )}
        </motion.div>

        {/* Referral Program */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="bg-[var(--card-bg)] backdrop-blur-xl border border-ethereal/20 rounded-2xl p-6"
          data-testid="referral-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <Gift size={24} className="text-ethereal" />
            <h3 className="font-heading text-ethereal tracking-widest uppercase text-sm">{t('profile.referral')}</h3>
          </div>
          <p className="text-muted-foreground text-sm mb-4">
            {t('profile.referralDesc', { credits: '50' })}
          </p>
          <div className="bg-[var(--card-bg)] rounded-lg p-3 mb-3">
            <p className="text-xs text-muted-foreground mb-1">{t('profile.yourLink')}</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-starlight text-sm font-mono truncate" data-testid="referral-link">
                {window.location.origin}/?ref={user.referral_code}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/?ref=${user.referral_code}`);
                  setRefCopied(true);
                  setTimeout(() => setRefCopied(false), 2000);
                }}
                className="p-2 bg-ethereal/10 border border-ethereal/30 rounded-lg hover:bg-ethereal/20 transition-all"
                data-testid="copy-referral-btn"
              >
                {refCopied ? <Check size={16} className="text-green-400" /> : <Copy size={16} className="text-ethereal" />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('profile.friendsInvited')}</span>
            <span className="text-ethereal font-medium" data-testid="referral-count">{user.referral_count || 0}</span>
          </div>
          <div className="flex items-center justify-between text-sm mt-1">
            <span className="text-muted-foreground">{t('profile.creditsEarned')}</span>
            <span className="text-starlight font-medium">{(user.referral_count || 0) * 50}</span>
          </div>
        </motion.div>

        {/* Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[var(--card-bg)] backdrop-blur-xl border border-secondary/20 rounded-2xl p-6"
          data-testid="stats-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <ChartBar size={24} className="text-secondary" />
            <h3 className="font-heading text-secondary tracking-widest uppercase text-sm">{t('profile.stats')}</h3>
          </div>
          {loading ? (
            <div className="flex justify-center py-6">
              <SpinnerGap size={32} className="text-secondary animate-spin" />
            </div>
          ) : stats ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[var(--card-bg)] rounded-xl p-4 text-center">
                <Cards size={24} className="text-secondary mx-auto mb-1" />
                <span className="text-2xl font-heading text-foreground block">{stats.tarot_readings}</span>
                <span className="text-xs text-muted-foreground">{t('profile.tarotCount')}</span>
              </div>
              <div className="bg-[var(--card-bg)] rounded-xl p-4 text-center">
                <Star size={24} className="text-starlight mx-auto mb-1" />
                <span className="text-2xl font-heading text-foreground block">{stats.horoscope_readings}</span>
                <span className="text-xs text-muted-foreground">{t('profile.horoscopeCount')}</span>
              </div>
              <div className="bg-[var(--card-bg)] rounded-xl p-4 text-center">
                <Hash size={24} className="text-accent mx-auto mb-1" />
                <span className="text-2xl font-heading text-foreground block">{stats.numerology_readings}</span>
                <span className="text-xs text-muted-foreground">{t('profile.numerologyCount')}</span>
              </div>
              <div className="bg-[var(--card-bg)] rounded-xl p-4 text-center">
                <Eye size={24} className="text-ethereal mx-auto mb-1" />
                <span className="text-2xl font-heading text-foreground block">{stats.oracle_messages}</span>
                <span className="text-xs text-muted-foreground">{t('profile.oracleCount')}</span>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground text-sm py-4">{t('profile.noData')}</p>
          )}
        </motion.div>

        {/* Settings (Placeholders) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-[var(--card-bg)] backdrop-blur-xl border border-[var(--glass-border)] rounded-2xl p-6"
          data-testid="settings-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <Gear size={24} className="text-muted-foreground" />
            <h3 className="font-heading text-foreground/80 tracking-widest uppercase text-sm">{t('profile.settings')}</h3>
          </div>
          <div className="space-y-3">
            {/* Theme toggle */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <SunDim size={20} className="text-muted-foreground" />
                <span className="text-sm text-foreground/80">{t('profile.lightTheme')}</span>
              </div>
              <div className="w-10 h-5 bg-white/10 rounded-full relative cursor-not-allowed opacity-50" data-testid="theme-toggle">
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-muted-foreground rounded-full" />
              </div>
            </div>
            {/* Music toggle */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <MusicNote size={20} className="text-muted-foreground" />
                <span className="text-sm text-foreground/80">{t('profile.bgMusic')}</span>
              </div>
              <div className="w-10 h-5 bg-white/10 rounded-full relative cursor-not-allowed opacity-50" data-testid="music-toggle">
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-muted-foreground rounded-full" />
              </div>
            </div>
            {/* Email preferences */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <EnvelopeOpen size={20} className="text-muted-foreground" />
                <span className="text-sm text-foreground/80">{t('profile.emailNotify')}</span>
              </div>
              <div className="w-10 h-5 bg-white/10 rounded-full relative cursor-not-allowed opacity-50" data-testid="email-toggle">
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-muted-foreground rounded-full" />
              </div>
            </div>
            <p className="text-muted-foreground/40 text-xs text-center pt-1">{t('profile.comingSoon')}</p>
          </div>
        </motion.div>

        {/* Subscription */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[var(--card-bg)] backdrop-blur-xl border border-amber-500/20 rounded-2xl p-6"
          data-testid="subscription-card"
        >
          <div className="flex items-center gap-3 mb-3">
            <Crown size={24} className="text-amber-400" />
            <h3 className="font-heading text-amber-400 tracking-widest uppercase text-sm">{t('profile.subscription')}</h3>
          </div>
          <p className="text-foreground/80 text-sm mb-3">
            {t('profile.subscriptionDesc')}
          </p>
          <button
            className="w-full bg-amber-500/10 border border-amber-500/30 text-amber-400
                       py-3 rounded-xl font-heading tracking-widest uppercase text-sm
                       opacity-60 cursor-not-allowed"
            disabled
            data-testid="subscribe-btn"
          >
            {t('profile.subscriptionPrice')}
          </button>
        </motion.div>

        {/* Askeza (Self-Discipline) Section */}
        <AskezaSection user={user} />

        {/* Horoscope Orders Section */}
        {horoscopeOrders.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-[var(--card-bg)] border border-starlight/10 rounded-2xl p-5 mb-4"
            data-testid="horoscope-orders-section"
          >
            <h3 className="font-heading text-sm tracking-widest uppercase text-starlight mb-3 flex items-center gap-2">
              <Package size={16} />
              {t('horoscope.myOrders') || 'Мои заказы'}
            </h3>
            <div className="space-y-2">
              {horoscopeOrders.map(order => {
                const statusColors = {
                  generating: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
                  generated: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
                  delivered: 'text-green-400 bg-green-400/10 border-green-400/20',
                  error: 'text-red-400 bg-red-400/10 border-red-400/20'
                };
                const statusLabels = {
                  generating: t('horoscope.statusPreparing') || 'Готовится',
                  generated: t('horoscope.statusReady') || 'Готов',
                  delivered: t('horoscope.statusDelivered') || 'Доставлен',
                  error: 'Ошибка'
                };
                const nameKey = `type_name_${language === 'uk' ? 'en' : language}`;
                return (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/3 border border-white/5"
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                           style={{ background: `${order.type_color}10`, border: `1px solid ${order.type_color}20` }}>
                        <Star size={14} style={{ color: order.type_color }} />
                      </div>
                      <div>
                        <p className="text-foreground text-sm">{order[nameKey] || order.type_name_ru}</p>
                        <p className="text-muted-foreground/50 text-[10px]">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-1 rounded-full border ${statusColors[order.status] || statusColors.generating}`}>
                      {statusLabels[order.status] || order.status}
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Logout at bottom */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center pb-8"
        >
          <button
            onClick={() => { onLogout(); onBack(); }}
            className="text-red-400/60 hover:text-red-400 text-sm transition-colors"
            data-testid="profile-logout-bottom-btn"
          >
            {t('profile.logoutAccount')}
          </button>
        </motion.div>
      </main>
    </div>
  );
};

// Beautiful Tarot Card Display
const TarotCardDisplay = ({ card, position, index }) => {
  const { t } = useLanguage();
  const [isFlipped, setIsFlipped] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsFlipped(false), index * 600 + 500);
    return () => clearTimeout(timer);
  }, [index]);

  // Check if Major Arcana (has AI art)
  const arcanaNumber = CARD_NAME_TO_NUMBER[card.name_ru] ?? CARD_NAME_TO_NUMBER[card.name] ?? null;
  const isMajor = arcanaNumber !== null && arcanaNumber !== undefined;
  const majorArtUrl = isMajor ? MAJOR_ARCANA_IMAGES[arcanaNumber] : null;

  // Check Minor Arcana (AI-generated images from minorArcana.js)
  const minorArtUrl = !isMajor ? (MINOR_ARCANA_LOOKUP[card.name_ru] || MINOR_ARCANA_LOOKUP[card.name] || null) : null;
  const artUrl = majorArtUrl || minorArtUrl;

  // Minor Arcana suit detection
  const getSuit = () => {
    for (const [suit, style] of Object.entries(SUIT_STYLES)) {
      if (card.name_ru?.includes(suit) || card.name?.includes(suit)) return style;
    }
    return { symbol: "✧", color: "#D4AF37", accent: "yellow" };
  };
  const suit = !isMajor ? getSuit() : null;

  const [showDetail, setShowDetail] = useState(false);
  
  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.3, duration: 0.6 }}
      className="flex flex-col items-center perspective-1000"
    >
      <motion.span 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: index * 0.3 + 0.8 }}
        className="text-starlight/80 text-sm mb-3 font-heading tracking-widest uppercase"
      >
        {position}
      </motion.span>
      
      <div 
        className="relative preserve-3d cursor-pointer group"
        style={{ 
          width: '160px', 
          height: '248px',
          transformStyle: 'preserve-3d',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => !isFlipped && setShowDetail(true)}
      >
        {/* Glow effect on hover */}
        <motion.div
          className="absolute -inset-4 rounded-2xl pointer-events-none"
          animate={{
            opacity: isHovered ? 1 : 0,
            scale: isHovered ? 1 : 0.9,
          }}
          transition={{ duration: 0.3 }}
          style={{
            background: card.reversed 
              ? 'radial-gradient(circle, rgba(239,68,68,0.4) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)',
            filter: 'blur(10px)',
          }}
        />
        
        <motion.div
          animate={{ 
            rotateY: isFlipped ? 180 : (card.reversed ? 180 : 0),
            scale: isHovered ? 1.08 : 1,
            y: isHovered ? -8 : 0,
          }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          style={{ transformStyle: 'preserve-3d' }}
          className="w-full h-full relative"
        >
          {/* Card Front — AI Art for Major, Stylized for Minor */}
          <div 
            className={`absolute inset-0 rounded-xl overflow-hidden border-2 
              ${card.reversed ? 'border-red-500/60' : 'border-starlight/60'}
              ${isHovered ? 'shadow-[0_0_50px_rgba(212,175,55,0.5)]' : 'shadow-[0_0_30px_rgba(212,175,55,0.3)]'}
              backface-hidden transition-shadow duration-300`}
            style={{ 
              backfaceVisibility: 'hidden',
              transform: card.reversed ? 'rotateZ(180deg)' : 'none'
            }}
          >
            {artUrl ? (
              /* Major Arcana — AI-generated art */
              <>
                <img 
                  src={artUrl} 
                  alt={card.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Dark overlay for name readability */}
                <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-3 text-center">
                  <p className="font-heading text-xs text-starlight uppercase tracking-[0.15em] leading-tight drop-shadow-lg">
                    {card.display_name || card.name_ru}
                  </p>
                  <p className="text-[9px] text-white/50 mt-0.5 italic">
                    {card.name}
                  </p>
                </div>
              </>
            ) : (
              /* Minor Arcana — Stylized CSS card */
              <>
                <div className="absolute inset-0 bg-gradient-to-br from-[#0a0b14] to-[#1a1520]" />
                <div className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage: `radial-gradient(circle at 50% 30%, ${suit.color}40 0%, transparent 50%)`
                  }}
                />
                {/* Ornate border pattern */}
                <div className="absolute inset-2 border border-starlight/20 rounded-lg" />
                <div className="absolute inset-3 border border-starlight/10 rounded-md" />
                {/* Suit symbol */}
                <div className="relative h-full flex flex-col items-center justify-between py-4 px-3 z-10">
                  <span className="text-starlight/40 text-xs">✦ ✦ ✦</span>
                  <motion.span
                    animate={{ scale: isHovered ? [1, 1.15, 1] : 1 }}
                    transition={{ duration: 0.5 }}
                    className="text-4xl"
                    style={{ filter: `drop-shadow(0 0 12px ${suit.color}80)` }}
                  >
                    {suit.symbol}
                  </motion.span>
                  <div className="text-center">
                    <p className="font-heading text-xs text-starlight uppercase tracking-[0.15em] leading-tight">
                      {card.display_name || card.name_ru}
                    </p>
                    <p className="text-[9px] text-muted-foreground/50 mt-0.5 italic">
                      {card.name}
                    </p>
                  </div>
                  <span className="text-starlight/40 text-xs">✦ ✦ ✦</span>
                </div>
                {/* Corner decorations */}
                <div className="absolute top-3 left-3 text-starlight/20 text-sm">♦</div>
                <div className="absolute top-3 right-3 text-starlight/20 text-sm">♦</div>
                <div className="absolute bottom-3 left-3 text-starlight/20 text-sm rotate-180">♦</div>
                <div className="absolute bottom-3 right-3 text-starlight/20 text-sm rotate-180">♦</div>
              </>
            )}
            {/* Reversed indicator */}
            {card.reversed && (
              <div className="absolute top-2 right-2 bg-red-500/80 text-white text-[9px] px-1.5 py-0.5 rounded font-bold z-20"
                style={{ transform: card.reversed ? 'rotateZ(180deg)' : 'none' }}>
                REV
              </div>
            )}
          </div>
          
          {/* Card Back */}
          <div 
            className={`
              absolute inset-0 rounded-xl overflow-hidden
              bg-gradient-to-br from-void-blue via-purple-900/50 to-void-blue
              border-2 border-starlight/40
              shadow-[0_0_20px_rgba(123,44,191,0.3)]
              backface-hidden
            `}
            style={{ 
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)'
            }}
          >
            {/* Mystical back pattern */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: `
                  repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(212,175,55,0.05) 10px, rgba(212,175,55,0.05) 20px),
                  repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(123,44,191,0.05) 10px, rgba(123,44,191,0.05) 20px)
                `
              }}
            />
            
            {/* Center design */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 animate-spin-slow">
                  <span className="absolute text-starlight/30 text-2xl" style={{ top: '-30px', left: '50%', transform: 'translateX(-50%)' }}>✧</span>
                  <span className="absolute text-starlight/30 text-2xl" style={{ bottom: '-30px', left: '50%', transform: 'translateX(-50%)' }}>✧</span>
                  <span className="absolute text-starlight/30 text-2xl" style={{ left: '-30px', top: '50%', transform: 'translateY(-50%)' }}>✧</span>
                  <span className="absolute text-starlight/30 text-2xl" style={{ right: '-30px', top: '50%', transform: 'translateY(-50%)' }}>✧</span>
                </div>
                <span className="text-4xl text-starlight/60">☽</span>
              </div>
            </div>
            
            {/* Border pattern */}
            <div className="absolute inset-3 border border-starlight/20 rounded-lg" />
            <div className="absolute inset-5 border border-starlight/10 rounded" />
          </div>
        </motion.div>
      </div>
      
      {/* Card meaning */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.3 + 1 }}
        className="mt-4 text-center max-w-[150px]"
      >
        {card.reversed && (
          <span className="text-red-400/80 text-xs font-medium block mb-1">
            ⟲ {t('divination.reversed')}
          </span>
        )}
        <p className="text-muted-foreground/70 text-xs italic leading-relaxed">
          {card.meaning}
        </p>
      </motion.div>
    </motion.div>
    
    {/* Card Detail Modal */}
    <AnimatePresence>
      {showDetail && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[999999] flex items-center justify-center p-6"
          onClick={() => setShowDetail(false)}
        >
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 25 }}
            className="relative z-10 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Large card view */}
            <div 
              className={`
                relative w-64 h-96 rounded-2xl overflow-hidden
                border-3 ${card.reversed ? 'border-red-500' : 'border-starlight'}
                shadow-[0_0_80px_rgba(212,175,55,0.5)]
              `}
              style={{ transform: card.reversed ? 'rotateZ(180deg)' : 'none' }}
            >
              {artUrl ? (
                /* Major Arcana — AI art in modal */
                <>
                  <img 
                    src={artUrl} 
                    alt={card.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/90 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 text-center" style={{ transform: card.reversed ? 'rotateZ(180deg)' : 'none' }}>
                    <p className="font-heading text-xl text-starlight uppercase tracking-[0.2em]">
                      {card.display_name || card.name_ru}
                    </p>
                    <p className="text-sm text-white/60 mt-1">{card.name}</p>
                  </div>
                </>
              ) : (
                /* Minor Arcana — stylized card in modal */
                <>
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0a0b14] to-[#1a1520]" />
                  <div className="absolute inset-0 bg-gradient-radial from-white/10 to-transparent" />
                  
                  <div className="relative h-full flex flex-col items-center justify-between p-8 z-10">
                    <div className="flex items-center gap-2">
                      <span className="text-starlight/60">✦</span>
                      <span className="text-starlight/40 text-xs">•</span>
                      <span className="text-starlight/60">✦</span>
                    </div>
                    
                    <motion.span 
                      animate={{ 
                        scale: [1, 1.05, 1],
                        textShadow: [
                          '0 0 30px rgba(212,175,55,0.5)',
                          '0 0 50px rgba(212,175,55,0.8)',
                          '0 0 30px rgba(212,175,55,0.5)'
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="text-8xl filter drop-shadow-lg"
                      style={{ transform: card.reversed ? 'rotateZ(180deg)' : 'none' }}
                    >
                      {suit?.symbol || '✧'}
                    </motion.span>
                    
                    <div className="text-center" style={{ transform: card.reversed ? 'rotateZ(180deg)' : 'none' }}>
                      <p className="font-heading text-xl text-starlight uppercase tracking-[0.2em]">
                        {card.display_name || card.name_ru}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">{card.name}</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-starlight/60">✦</span>
                      <span className="text-starlight/40 text-xs">•</span>
                      <span className="text-starlight/60">✦</span>
                    </div>
                  </div>
                  
                  <div className="absolute top-3 left-3 text-starlight/40 text-2xl">♦</div>
                  <div className="absolute top-3 right-3 text-starlight/40 text-2xl">♦</div>
                  <div className="absolute bottom-3 left-3 text-starlight/40 text-2xl rotate-180">♦</div>
                  <div className="absolute bottom-3 right-3 text-starlight/40 text-2xl rotate-180">♦</div>
                </>
              )}
              
              {/* Reversed indicator */}
              {card.reversed && (
                <div className="absolute top-3 right-3 bg-red-500/80 text-white text-xs px-2 py-1 rounded font-bold z-20"
                  style={{ transform: card.reversed ? 'rotateZ(180deg)' : 'none' }}>
                  REV
                </div>
              )}
            </div>
            
            {/* Card info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-8 text-center max-w-md"
            >
              <p className="font-heading text-starlight tracking-widest uppercase text-sm mb-2">
                {position}
              </p>
              {card.reversed && (
                <span className="inline-block bg-red-500/20 text-red-400 text-xs px-3 py-1 rounded-full mb-3">
                  ⟲ {t('divination.reversedPosition')}
                </span>
              )}
              <p className="text-foreground/80 leading-relaxed">
                {card.meaning}
              </p>
            </motion.div>
            
            <button
              onClick={() => setShowDetail(false)}
              className="mt-6 text-muted-foreground hover:text-foreground transition-colors text-sm"
            >
              {t('divination.clickToClose')}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

// Apple-style Scroll Wheel Picker
const WheelPicker = ({ items, value, onChange, width = 'w-20' }) => {
  const containerRef = useRef(null);
  const ITEM_H = 40;
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startScroll = useRef(0);

  const selectedIdx = items.indexOf(value);

  useEffect(() => {
    if (containerRef.current && selectedIdx >= 0) {
      containerRef.current.scrollTop = selectedIdx * ITEM_H;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = () => {
    if (!containerRef.current) return;
    const idx = Math.round(containerRef.current.scrollTop / ITEM_H);
    const clamped = Math.max(0, Math.min(idx, items.length - 1));
    if (items[clamped] !== value) {
      onChange(items[clamped]);
    }
  };

  return (
    <div className={`relative ${width} h-[160px] overflow-hidden`}>
      {/* Highlight bar */}
      <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 h-[40px] bg-secondary/15 border-y border-secondary/30 rounded-lg pointer-events-none z-10" />
      {/* Fade edges */}
      <div className="absolute top-0 left-0 right-0 h-[60px] bg-gradient-to-b from-[var(--color-background)] to-transparent z-10 pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-[60px] bg-gradient-to-t from-[var(--color-background)] to-transparent z-10 pointer-events-none" />
      {/* Scroll container */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto scrollbar-hide snap-y snap-mandatory"
        style={{ scrollSnapType: 'y mandatory', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {/* Top/bottom padding for centering */}
        <div style={{ height: '60px' }} />
        {items.map((item, i) => (
          <div
            key={`${item}-${i}`}
            className={`h-[40px] flex items-center justify-center snap-center transition-all cursor-pointer
              ${item === value ? 'text-foreground text-lg font-semibold' : 'text-muted-foreground/50 text-sm'}`}
            onClick={() => {
              onChange(item);
              if (containerRef.current) {
                containerRef.current.scrollTo({ top: i * ITEM_H, behavior: 'smooth' });
              }
            }}
          >
            {item}
          </div>
        ))}
        <div style={{ height: '60px' }} />
      </div>
    </div>
  );
};

// Tarot Question Selector Modal - New enhanced UI
const TarotQuestionModal = ({ title, onSubmit, onClose }) => {
  const { t, language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [customQuestion, setCustomQuestion] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Categories with Phosphor icons
  const categoryIcons = {
    love: <Heart size={22} weight="duotone" />,
    career: <Briefcase size={22} weight="duotone" />,
    money: <Coin size={22} weight="duotone" />,
    health: <Heartbeat size={22} weight="duotone" />,
    decisions: <Sparkle size={22} weight="duotone" />,
    purpose: <Star size={22} weight="duotone" />,
    asceticism: <Fire size={22} weight="duotone" />
  };
  
  const categories = [
    { id: 'love', label: t('divination.categoryLove') || 'Любовь' },
    { id: 'career', label: t('divination.categoryCareer') || 'Карьера' },
    { id: 'money', label: t('divination.categoryMoney') || 'Финансы' },
    { id: 'asceticism', label: t('divination.categoryAsceticism') || 'Аскезы' },
    { id: 'decisions', label: t('divination.categoryDecisions') || 'Решения' },
    { id: 'purpose', label: t('divination.categoryPurpose') || 'Призвание' },
  ];

  // Load initial general questions
  useEffect(() => {
    setSuggestedQuestions(getRandomQuestions('general', 3, language));
  }, [language]);

  // Update questions when category changes
  const handleCategorySelect = (categoryId) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null);
      setSuggestedQuestions(getRandomQuestions('general', 3, language));
    } else {
      setSelectedCategory(categoryId);
      setSuggestedQuestions(getRandomQuestions(categoryId, 3, language));
    }
    setShowCustomInput(false);
    setCustomQuestion('');
  };

  // Shuffle questions
  const handleShuffle = () => {
    const category = selectedCategory || 'general';
    setSuggestedQuestions(getRandomQuestions(category, 3, language));
  };

  // Select a suggested question
  const handleQuestionSelect = (question) => {
    setCustomQuestion(question);
    setShowCustomInput(true);
  };

  // Submit
  const handleSubmit = (e) => {
    e.preventDefault();
    const topic = customQuestion.trim() || (selectedCategory ? categories.find(c => c.id === selectedCategory)?.label : null);
    onSubmit({ topic });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative bg-[var(--color-card)] backdrop-blur-xl border border-secondary/30 
                   rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        data-testid="tarot-question-modal"
      >
        <div className="p-6 pb-8">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mb-5 sm:hidden" />
          
          <h2 className="font-heading text-xl tracking-widest uppercase text-secondary text-center mb-1">
            {title}
          </h2>
          <p className="text-muted-foreground text-sm text-center mb-6">
            {t('divination.topicHint')}
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Category Selection */}
            <div>
              <div className="grid grid-cols-3 gap-2">
                {categories.map(cat => (
                  <button 
                    key={cat.id} 
                    type="button" 
                    onClick={() => handleCategorySelect(cat.id)}
                    className={`px-3 py-3 rounded-xl text-sm border transition-all flex flex-col items-center gap-1.5 ${
                      selectedCategory === cat.id 
                        ? 'bg-secondary/20 border-secondary/50 text-secondary' 
                        : 'bg-[var(--card-bg)] border-[var(--glass-border)] text-muted-foreground hover:border-secondary/30 hover:text-secondary/70'
                    }`}
                    data-testid={`tarot-category-${cat.id}`}
                  >
                    <span className={selectedCategory === cat.id ? 'text-secondary' : 'text-muted-foreground/70'}>
                      {categoryIcons[cat.id]}
                    </span>
                    <span className="text-xs">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Question Suggestions */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground/70">
                  {t('divination.questionSuggestions') || 'Или выберите вопрос:'}
                </p>
                <button
                  type="button"
                  onClick={handleShuffle}
                  className="flex items-center gap-1 text-xs text-secondary/70 hover:text-secondary transition-colors"
                  data-testid="shuffle-questions-btn"
                >
                  <ArrowsClockwise size={14} />
                  {t('divination.shuffleQuestions') || 'Другие'}
                </button>
              </div>
              
              <div className="space-y-2">
                {suggestedQuestions.map((question, idx) => (
                  <motion.button
                    key={`${selectedCategory}-${idx}-${question.slice(0, 20)}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    type="button"
                    onClick={() => handleQuestionSelect(question)}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm border transition-all ${
                      customQuestion === question
                        ? 'bg-secondary/15 border-secondary/40 text-foreground'
                        : 'bg-[var(--card-bg)] border-[var(--glass-border)] text-muted-foreground hover:border-secondary/30 hover:text-foreground'
                    }`}
                    data-testid={`suggested-question-${idx}`}
                  >
                    "{question}"
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Custom Question Input */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <PencilSimple size={14} className="text-muted-foreground/50" />
                <p className="text-xs text-muted-foreground/70">
                  {t('divination.yourQuestion') || 'Ваш вопрос'}
                </p>
              </div>
              <textarea
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                placeholder={t('divination.questionPlaceholder') || 'Напишите, что вас волнует...'}
                rows={2}
                className="w-full bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm text-foreground 
                           placeholder:text-muted-foreground focus:border-starlight/60 focus:outline-none resize-none transition-colors"
                data-testid="custom-question-input"
              />
            </div>

            {/* Submit Button with price */}
            <button
              type="submit"
              className="w-full bg-secondary/10 border border-secondary/50 text-secondary
                         py-4 rounded-xl font-heading tracking-widest uppercase
                         hover:bg-secondary/20 transition-all active:scale-[0.98]
                         flex items-center justify-center gap-3"
              data-testid="tarot-submit-btn"
            >
              <span>{t('divination.continue')}</span>
              <span className="text-secondary/60 text-sm flex items-center gap-1">• 10 <MarioCoin size={14} /></span>
            </button>
          </form>
          
          {/* Back button */}
          <button
            onClick={onClose}
            className="w-full text-center text-muted-foreground text-sm mt-4 hover:text-foreground transition-colors"
            data-testid="tarot-back-btn"
          >
            {t('chat.back') || 'Назад'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============== CREDIT SHOP MODAL ==============
const CREDIT_PACKAGES = [
  { id: 'starter', price: '5€', credits: 100, perCredit: '0.05€', bonus: null, checkoutUrl: 'https://onima.lemonsqueezy.com/buy/1340406' },
  { id: 'standard', price: '10€', credits: 250, perCredit: '0.04€', bonus: '+25%', checkoutUrl: 'https://onima.lemonsqueezy.com/buy/1340426' },
  { id: 'plus', price: '15€', credits: 400, perCredit: '0.037€', bonus: '+33%', checkoutUrl: 'https://onima.lemonsqueezy.com/buy/1340432' },
  { id: 'pro', price: '40€', credits: 1000, perCredit: '0.04€', bonus: '+60%', checkoutUrl: 'https://onima.lemonsqueezy.com/buy/1340439' },
];

const CreditShopModal = ({ user, onClose }) => {
  const { t } = useLanguage();

  const handleBuy = (pkg) => {
    const url = new URL(pkg.checkoutUrl);
    if (user?.email) url.searchParams.set('checkout[email]', user.email);
    window.open(url.toString(), '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-void-blue/95 backdrop-blur-xl border border-starlight/20 rounded-2xl p-6 max-w-md w-full shadow-[0_0_60px_rgba(212,175,55,0.15)]"
        data-testid="credit-shop-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10">✕</button>

        <div className="text-center mb-6">
          <MarioCoin size={36} className="mx-auto mb-2" />
          <h2 className="font-heading text-xl tracking-widest uppercase text-starlight">
            {t('profile.buyCredits') || 'Купить кредиты'}
          </h2>
          {user && (
            <p className="text-muted-foreground text-sm mt-1">
              {t('profile.balance') || 'Баланс'}: <span className="text-starlight font-medium">{user.total_credits}</span>
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {CREDIT_PACKAGES.map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => handleBuy(pkg)}
              className="bg-starlight/5 border border-starlight/20 rounded-xl p-4 text-center
                         hover:border-starlight/40 hover:bg-starlight/10 transition-all group
                         hover:scale-105 active:scale-95"
              data-testid={`shop-buy-${pkg.credits}`}
            >
              {pkg.bonus && (
                <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full mb-2 inline-block">
                  {pkg.bonus}
                </span>
              )}
              <span className="text-2xl font-heading text-starlight block">{pkg.credits}</span>
              <span className="text-xs text-muted-foreground block mt-1">{t('profile.credits') || 'кредитов'}</span>
              <span className="text-sm text-starlight font-medium block mt-2">{pkg.price}</span>
              <span className="text-[10px] text-muted-foreground/60 block">{pkg.perCredit}/{t('profile.credit') || 'кр.'}</span>
            </button>
          ))}
        </div>

        <p className="text-center text-muted-foreground/60 text-xs mt-4">
          {t('profile.securePayment') || 'Secure payment via LemonSqueezy'}
        </p>
      </motion.div>
    </motion.div>
  );
};

// ============== HOROSCOPE SELECTION MODAL ==============
const HOROSCOPE_ICONS = {
  globe: Globe, map: MapPin, currency: CurrencyDollar, heart: Heart, star: StarFour
};

const HoroscopeSelectionModal = ({ onSelectStandard, onSelectIndividual, onClose, user }) => {
  const { t, language } = useLanguage();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/horoscopes/types`)
      .then(res => { setTypes(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const nameKey = `name_${language}`;
  const subtitleKey = `subtitle_${language}`;
  const descKey = `description_${language}`;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-void-blue/95 backdrop-blur-xl border border-starlight/20 rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-[0_0_60px_rgba(212,175,55,0.15)]"
        data-testid="horoscope-selection-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10">
          ✕
        </button>

        <div className="text-center mb-6">
          <Star size={36} weight="duotone" className="text-starlight mx-auto mb-2" />
          <h2 className="font-heading text-xl tracking-widest uppercase text-starlight">
            {t('horoscope.selectTitle') || 'Выберите гороскоп'}
          </h2>
        </div>

        {/* Standard horoscope */}
        <button
          onClick={onSelectStandard}
          className="w-full mb-4 p-4 rounded-xl border border-starlight/20 bg-starlight/5 hover:bg-starlight/10 hover:border-starlight/40 transition-all text-left group"
          data-testid="standard-horoscope-btn"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-starlight font-heading tracking-wider text-sm uppercase">
                {t('horoscope.standard') || 'Стандартный гороскоп'}
              </h3>
              <p className="text-muted-foreground text-xs mt-1">
                {t('horoscope.standardDesc') || 'Персональный прогноз на основе даты рождения'}
              </p>
            </div>
            <span className="text-starlight/60 text-xs font-mono">10 {t('profile.credits') || 'кр.'}</span>
          </div>
        </button>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-starlight/15 to-transparent" />
          <span className="text-starlight/40 text-xs tracking-widest uppercase font-heading">
            {t('horoscope.premium') || 'Premium'}
          </span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-starlight/15 to-transparent" />
        </div>

        {/* Individual horoscopes */}
        {loading ? (
          <div className="flex justify-center py-8"><SpinnerGap size={32} className="text-starlight animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {types.map(htype => {
              const Icon = HOROSCOPE_ICONS[htype.icon] || Star;
              return (
                <div key={htype.id} className="relative">
                  <button
                    disabled
                    className="w-full p-4 rounded-xl border border-white/8 bg-white/3 text-left opacity-50 cursor-not-allowed"
                    data-testid={`horoscope-type-${htype.id}`}
                    style={{ borderColor: `${htype.color}15` }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${htype.color}10`, border: `1px solid ${htype.color}25` }}
                      >
                        <Icon size={20} weight="duotone" style={{ color: htype.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-foreground text-sm font-medium">{htype[nameKey] || htype.name_ru}</h4>
                        <p className="text-muted-foreground text-[11px] mt-0.5">{htype[subtitleKey] || htype.subtitle_ru}</p>
                        <p className="text-muted-foreground/60 text-[10px] mt-1 line-clamp-2">{htype[descKey] || htype.description_ru}</p>
                      </div>
                      <span className="text-xs font-mono flex-shrink-0" style={{ color: `${htype.color}99` }}>
                        {htype.price}
                      </span>
                    </div>
                  </button>
                  <div className="absolute top-2 right-2 bg-amber-500/20 border border-amber-500/40 rounded-full px-2.5 py-0.5">
                    <span className="text-amber-400 text-[10px] font-heading tracking-wider uppercase">
                      {t('horoscope.comingSoon') || 'Скоро'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-center text-muted-foreground/30 text-[10px] mt-4">
          {t('horoscope.deliveryNote') || 'Индивидуальные гороскопы доставляются на email в течение 1-3 рабочих дней'}
        </p>
      </motion.div>
    </motion.div>
  );
};

// ============== INDIVIDUAL HOROSCOPE FORM ==============
const IndividualHoroscopeForm = ({ horoscopeType, onSubmit, onClose, onBack, savedProfile }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const nameKey = `name_${language}`;
  const p = savedProfile || {};

  // Pre-fill from saved profile where possible
  useEffect(() => {
    const prefill = {};
    if (p.birth_date) {
      const parts = p.birth_date.split('.');
      if (parts.length === 3) prefill.birth_date = p.birth_date;
    }
    if (p.birth_time) prefill.birth_time = p.birth_time;
    if (p.birth_city) prefill.birth_city = p.birth_city;
    if (p.birth_country) prefill.birth_country = p.birth_country;
    if (p.current_city) prefill.current_city = p.current_city;
    setFormData(prefill);
  }, [p.birth_date, p.birth_time, p.birth_city, p.birth_country, p.current_city]);

  const fields = horoscopeType.required_fields || [];
  const questions = horoscopeType.questions_ru || [];

  const fieldLabels = {
    full_name: 'Полное имя',
    birth_date: 'Дата рождения (дд.мм.гггг)',
    birth_time: 'Время рождения (чч:мм)',
    birth_city: 'Город рождения',
    birth_country: 'Страна рождения',
    current_city: 'Текущий город',
    target_city: 'Город переезда',
    profession: 'Профессия / сфера деятельности',
    person1_name: 'Имя первого человека',
    person1_birth_date: 'Дата рождения первого',
    person1_birth_time: 'Время рождения первого',
    person1_birth_city: 'Город рождения первого',
    person2_name: 'Имя второго человека',
    person2_birth_date: 'Дата рождения второго',
    person2_birth_time: 'Время рождения второго',
    person2_birth_city: 'Город рождения второго'
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    const missing = fields.filter(f => !formData[f]?.trim());
    if (missing.length > 0) {
      setError(t('horoscope.fillAll') || 'Заполните все обязательные поля');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error');
      setIsSubmitting(false);
    }
  };

  // Also add an optional "question" field at the end
  const allFields = [...fields];
  const hasQuestion = !allFields.includes('question');

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-void-blue/95 backdrop-blur-xl border border-starlight/20 rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-[0_0_60px_rgba(212,175,55,0.15)]"
        data-testid="individual-horoscope-form"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-10">
          ✕
        </button>

        {/* Header */}
        <div className="text-center mb-5">
          <button onClick={onBack} className="absolute top-4 left-4 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div
            className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center"
            style={{ background: `${horoscopeType.color}10`, border: `1px solid ${horoscopeType.color}30` }}
          >
            {(() => { const Icon = HOROSCOPE_ICONS[horoscopeType.icon] || Star; return <Icon size={24} weight="duotone" style={{ color: horoscopeType.color }} />; })()}
          </div>
          <h2 className="font-heading text-lg tracking-wider uppercase" style={{ color: horoscopeType.color }}>
            {horoscopeType[nameKey] || horoscopeType.name_ru}
          </h2>
          <p className="text-muted-foreground text-xs mt-1">
            {horoscopeType.price} {t('profile.credits') || 'кредитов'}
          </p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {allFields.map(field => (
            <div key={field}>
              <label className="block text-muted-foreground text-xs mb-1">
                {fieldLabels[field] || field.replace(/_/g, ' ')}
              </label>
              <input
                type="text"
                value={formData[field] || ''}
                onChange={(e) => handleChange(field, e.target.value)}
                className="w-full bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3
                           text-foreground text-sm focus:border-starlight/60 focus:outline-none transition-colors
                           placeholder:text-muted-foreground"
                placeholder={fieldLabels[field] || ''}
                data-testid={`horoscope-field-${field}`}
              />
            </div>
          ))}

          {/* Optional question */}
          {hasQuestion && (
            <div>
              <label className="block text-muted-foreground text-xs mb-1">
                {t('horoscope.specificQuestion') || 'Конкретный вопрос (необязательно)'}
              </label>
              <textarea
                value={formData.specific_question || ''}
                onChange={(e) => handleChange('specific_question', e.target.value)}
                rows={2}
                className="w-full bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3
                           text-foreground text-sm focus:border-starlight/60 focus:outline-none resize-none transition-colors
                           placeholder:text-muted-foreground"
                placeholder={t('horoscope.questionPlaceholder') || 'Что вас волнует больше всего?'}
                data-testid="horoscope-question-field"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-2 bg-starlight/10 border border-starlight/50 text-starlight
                       py-3 rounded-xl font-heading tracking-widest text-sm uppercase
                       hover:bg-starlight/20 hover:shadow-[0_0_20px_rgba(212,175,55,0.2)]
                       disabled:opacity-50 transition-all active:scale-95"
            data-testid="horoscope-submit-btn"
          >
            {isSubmitting ? (
              <SpinnerGap size={20} className="animate-spin mx-auto" />
            ) : (
              `${t('horoscope.orderBtn') || 'Заказать'} — ${horoscopeType.price} ${t('profile.credits') || 'кр.'}`
            )}
          </button>
        </form>

        <p className="text-center text-muted-foreground/30 text-[10px] mt-3">
          {t('horoscope.deliveryNote') || 'Результат будет отправлен на email в течение 1-3 рабочих дней'}
        </p>
      </motion.div>
    </motion.div>
  );
};

// ============== HOROSCOPE ORDER SUCCESS ==============
const HoroscopeOrderSuccess = ({ orderData, onClose }) => {
  const { t } = useLanguage();
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="relative bg-void-blue/95 backdrop-blur-xl border border-starlight/30 rounded-2xl p-8 max-w-md w-full text-center shadow-[0_0_60px_rgba(212,175,55,0.2)]"
        data-testid="horoscope-order-success"
      >
        <div className="w-16 h-16 rounded-full bg-starlight/10 border border-starlight/30 mx-auto mb-4 flex items-center justify-center">
          <Clock size={32} weight="duotone" className="text-starlight" />
        </div>
        <h2 className="font-heading text-xl tracking-wider uppercase text-starlight mb-3">
          {t('horoscope.orderReceived') || 'Заказ принят'}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed mb-2">
          {t('horoscope.orderMessage') || 'Оракул приступил к расчёту вашего индивидуального гороскопа.'}
        </p>
        <p className="text-muted-foreground/60 text-xs leading-relaxed mb-6">
          {t('horoscope.deliveryTime') || 'Для подготовки точного прогноза нам потребуется от 1 до 3 рабочих дней. Готовый гороскоп будет отправлен на вашу почту.'}
        </p>

        <div className="bg-starlight/5 border border-starlight/15 rounded-xl p-3 mb-5">
          <div className="flex items-center justify-center gap-2">
            <Package size={16} className="text-starlight/60" />
            <span className="text-starlight/70 text-xs font-mono">{t('horoscope.orderStatus') || 'Статус: Подготовка'}</span>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full bg-starlight/10 border border-starlight/40 text-starlight
                     py-3 rounded-xl font-heading tracking-widest text-sm uppercase
                     hover:bg-starlight/20 transition-all active:scale-95"
          data-testid="order-success-close"
        >
          {t('horoscope.understood') || 'Понятно'}
        </button>
      </motion.div>
    </motion.div>
  );
};

// Date Input Modal - Apple-style with saved profile and topic selection
const DateInputModal = ({ title, onSubmit, onClose, isHoroscope = false, savedProfile, user, isTarot = false }) => {
  const { t } = useLanguage();
  
  // Pre-fill from saved profile
  const p = savedProfile || {};
  const savedParts = p.birth_date ? p.birth_date.split('.') : [];
  
  const [day, setDay] = useState(savedParts[0] || '15');
  const [month, setMonth] = useState(savedParts[1] || '06');
  const [year, setYear] = useState(savedParts[2] || '1990');
  const [showExtended, setShowExtended] = useState(false);
  // Extended fields - pre-fill from profile
  const [birthCountry, setBirthCountry] = useState(p.birth_country || '');
  const [birthCity, setBirthCity] = useState(p.birth_city || '');
  const [birthTime, setBirthTime] = useState(p.birth_time || '');
  const [currentCity, setCurrentCity] = useState(p.current_city || '');
  const [focusArea, setFocusArea] = useState('');
  const [tone, setTone] = useState('');
  const [familyStatus, setFamilyStatus] = useState(p.family_status || '');
  const [children, setChildren] = useState(p.children || '');
  
  // Topic selection
  const [selectedTopic, setSelectedTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const days = Array.from({length: 31}, (_, i) => String(i + 1).padStart(2, '0'));
  const months = Array.from({length: 12}, (_, i) => String(i + 1).padStart(2, '0'));
  const years = Array.from({length: new Date().getFullYear() + 5 - 1900 + 1}, (_, i) => String(new Date().getFullYear() + 5 - i));
  
  // Predefined topics
  const topicOptions = [
    { id: 'love', label: t('divination.topicLove') },
    { id: 'money', label: t('divination.topicMoney') },
    { id: 'work', label: t('divination.topicWork') },
    { id: 'asceticism', label: t('divination.topicAsceticism') },
    { id: 'future', label: t('divination.topicFuture') },
    { id: 'decision', label: t('divination.topicDecision') },
  ];
  
  const getFinalTopic = () => {
    if (showCustomInput && customTopic.trim()) {
      return customTopic.trim();
    }
    if (selectedTopic) {
      const found = topicOptions.find(o => o.id === selectedTopic);
      return found ? found.label : selectedTopic;
    }
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const topic = getFinalTopic();
    
    // For Tarot - only topic needed
    if (isTarot) {
      onSubmit({ topic });
      return;
    }
    
    const birthDate = `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
    
    // Save profile data for logged-in users
    const profileData = {
      birth_date: birthDate,
      ...(birthCountry && { birth_country: birthCountry }),
      ...(birthCity && { birth_city: birthCity }),
      ...(birthTime && { birth_time: birthTime }),
      ...(currentCity && { current_city: currentCity }),
      ...(familyStatus && { family_status: familyStatus }),
      ...(children && { children }),
    };
    
    if (user?.id) {
      axios.post(`${API}/user/divination-profile/${user.id}`, { profile: profileData }).catch(() => {});
    }
    
    if (isHoroscope) {
      onSubmit({
        birth_date: birthDate,
        extended: showExtended,
        ...profileData,
        ...(focusArea && { focus_area: focusArea }),
        ...(tone && { tone }),
        ...(topic && { topic }),
      });
    } else {
      // Numerology
      onSubmit({
        birth_date: birthDate,
        ...(topic && { topic }),
      });
    }
  };

  const focusOptions = [
    { id: 'work', label: t('divination.focusWork') },
    { id: 'money', label: t('divination.focusMoney') },
    { id: 'relationships', label: t('divination.focusRelationships') },
    { id: 'asceticism', label: t('divination.focusAsceticism') },
    { id: 'growth', label: t('divination.focusGrowth') },
  ];
  const toneOptions = [
    { id: 'soft', label: t('divination.toneSoft') },
    { id: 'neutral', label: t('divination.toneNeutral') },
    { id: 'direct', label: t('divination.toneDirect') },
  ];
  const familyOptions = [
    { id: 'single', label: t('divination.statusSingle') },
    { id: 'relationship', label: t('divination.statusRelationship') },
    { id: 'married', label: t('divination.statusMarried') },
    { id: 'divorced', label: t('divination.statusDivorced') },
    { id: 'widowed', label: t('divination.statusWidowed') },
  ];
  const childrenOptions = [
    { id: 'none', label: t('divination.childrenNone') },
    { id: '1', label: t('divination.children1') },
    { id: '2', label: t('divination.children2') },
    { id: '3+', label: t('divination.children3plus') },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative bg-[var(--color-card)] backdrop-blur-xl border border-secondary/30 
                   rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        data-testid="date-input-modal"
      >
        <div className="p-6 pb-8">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mb-5 sm:hidden" />
          
          <h2 className="font-heading text-xl tracking-widest uppercase text-secondary text-center mb-1">
            {title}
          </h2>
          <p className="text-muted-foreground text-sm text-center mb-5">
            {isTarot ? t('divination.topicHint') : t('divination.enterBirthDate')}
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Topic Selection (for all types) */}
            <div>
              <p className="text-xs text-muted-foreground/60 mb-2">{t('divination.topicLabel')}</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {topicOptions.map(opt => (
                  <button 
                    key={opt.id} 
                    type="button" 
                    onClick={() => {
                      setSelectedTopic(selectedTopic === opt.id ? '' : opt.id);
                      setShowCustomInput(false);
                      setCustomTopic('');
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                      selectedTopic === opt.id 
                        ? 'bg-secondary/20 border-secondary/50 text-secondary' 
                        : 'bg-[var(--card-bg)] border-[var(--glass-border)] text-muted-foreground hover:border-secondary/30'
                    }`}
                    data-testid={`topic-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCustomInput(!showCustomInput);
                    setSelectedTopic('');
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                    showCustomInput 
                      ? 'bg-secondary/20 border-secondary/50 text-secondary' 
                      : 'bg-[var(--card-bg)] border-[var(--glass-border)] text-muted-foreground hover:border-secondary/30'
                  }`}
                  data-testid="topic-custom"
                >
                  {t('divination.topicCustom')}
                </button>
              </div>
              
              {showCustomInput && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                  <input
                    type="text"
                    value={customTopic}
                    onChange={(e) => setCustomTopic(e.target.value)}
                    placeholder={t('divination.topicPlaceholder')}
                    className="w-full bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm text-foreground 
                               placeholder:text-muted-foreground focus:border-starlight/60 focus:outline-none transition-colors"
                    data-testid="custom-topic-input"
                    autoFocus
                  />
                </motion.div>
              )}
            </div>
            
            {/* Date wheels - only for non-Tarot */}
            {!isTarot && (
              <div className="flex justify-center gap-1 items-center">
                <WheelPicker items={days} value={day} onChange={setDay} width="w-16" />
                <span className="text-muted-foreground/30 text-xl px-1">/</span>
                <WheelPicker items={months} value={month} onChange={setMonth} width="w-16" />
                <span className="text-muted-foreground/30 text-xl px-1">/</span>
                <WheelPicker items={years} value={year} onChange={setYear} width="w-20" />
              </div>
            )}

            {/* Extended horoscope toggle (only for horoscope) */}
            {isHoroscope && !showExtended && (
              <button type="button" onClick={() => setShowExtended(true)}
                className="w-full text-sm text-secondary/70 hover:text-secondary transition-colors py-2"
                data-testid="show-extended-btn"
              >
                {t('divination.extendedHoroscope')} &darr;
              </button>
            )}

            {/* Extended personalization fields */}
            {isHoroscope && showExtended && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-4 pt-2 border-t border-[var(--glass-border)]">
                <p className="text-xs text-secondary/60 text-center">{t('divination.improveAccuracy')}</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" value={birthCountry} onChange={(e) => setBirthCountry(e.target.value)}
                    placeholder={t('divination.birthCountry')} data-testid="birth-country-input"
                    className="bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-starlight/60 focus:outline-none transition-colors" />
                  <input type="text" value={birthCity} onChange={(e) => setBirthCity(e.target.value)}
                    placeholder={t('divination.birthCity')} data-testid="birth-city-input"
                    className="bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-starlight/60 focus:outline-none transition-colors" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" value={birthTime} onChange={(e) => setBirthTime(e.target.value)}
                    placeholder={t('divination.birthTime')} data-testid="birth-time-input"
                    className="bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-starlight/60 focus:outline-none transition-colors" />
                  <input type="text" value={currentCity} onChange={(e) => setCurrentCity(e.target.value)}
                    placeholder={t('divination.currentCity')} data-testid="current-city-input"
                    className="bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-starlight/60 focus:outline-none transition-colors" />
                </div>
                
                {/* Family Status */}
                <div>
                  <p className="text-xs text-muted-foreground/50 mb-2">{t('divination.familyStatus')}</p>
                  <div className="flex flex-wrap gap-2">
                    {familyOptions.map(opt => (
                      <button key={opt.id} type="button" onClick={() => setFamilyStatus(familyStatus === opt.id ? '' : opt.id)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${familyStatus === opt.id ? 'bg-secondary/20 border-secondary/50 text-secondary' : 'bg-[var(--card-bg)] border-[var(--glass-border)] text-muted-foreground'}`}
                        data-testid={`family-${opt.id}`}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>

                {/* Children */}
                <div>
                  <p className="text-xs text-muted-foreground/50 mb-2">{t('divination.children')}</p>
                  <div className="flex flex-wrap gap-2">
                    {childrenOptions.map(opt => (
                      <button key={opt.id} type="button" onClick={() => setChildren(children === opt.id ? '' : opt.id)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${children === opt.id ? 'bg-secondary/20 border-secondary/50 text-secondary' : 'bg-[var(--card-bg)] border-[var(--glass-border)] text-muted-foreground'}`}
                        data-testid={`children-${opt.id}`}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>
                
                {/* Focus area */}
                <div>
                  <p className="text-xs text-muted-foreground/50 mb-2">{t('divination.focusArea')}</p>
                  <div className="flex flex-wrap gap-2">
                    {focusOptions.map(opt => (
                      <button key={opt.id} type="button" onClick={() => setFocusArea(focusArea === opt.id ? '' : opt.id)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${focusArea === opt.id ? 'bg-secondary/20 border-secondary/50 text-secondary' : 'bg-[var(--card-bg)] border-[var(--glass-border)] text-muted-foreground'}`}
                        data-testid={`focus-${opt.id}`}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>

                {/* Tone */}
                <div>
                  <p className="text-xs text-muted-foreground/50 mb-2">{t('divination.tone')}</p>
                  <div className="flex gap-2">
                    {toneOptions.map(opt => (
                      <button key={opt.id} type="button" onClick={() => setTone(tone === opt.id ? '' : opt.id)}
                        className={`flex-1 px-3 py-1.5 rounded-full text-xs border transition-all ${tone === opt.id ? 'bg-secondary/20 border-secondary/50 text-secondary' : 'bg-[var(--card-bg)] border-[var(--glass-border)] text-muted-foreground'}`}
                        data-testid={`tone-${opt.id}`}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>

                <button type="button" onClick={() => setShowExtended(false)}
                  className="w-full text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                >{t('divination.hide')}</button>
              </motion.div>
            )}
            
            <button type="submit"
              className="w-full bg-secondary/10 border border-secondary/50 text-secondary
                         py-4 rounded-xl font-heading tracking-widest uppercase
                         hover:bg-secondary/20 hover:shadow-[0_0_20px_rgba(123,44,191,0.3)]
                         transition-all duration-300 active:scale-95
                         flex items-center justify-center gap-3"
              data-testid="date-submit-btn"
            >
              <span>{showExtended ? t('divination.extendedHoroscope') : t('divination.continue')}</span>
              <span className="text-secondary/60 text-sm flex items-center gap-1">• 10 <MarioCoin size={14} /></span>
            </button>
          </form>
          
          <button onClick={onClose}
            className="mt-4 w-full text-muted-foreground text-sm hover:text-foreground transition-colors py-2"
          >{t('chat.back')}</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Divination Result Page
const DivinationResult = ({ result, onBack, user, onNewReading, onClarifyQuestion, onShowAuth }) => {
  const { t } = useLanguage();
  const positions = [t('divination.past'), t('divination.present'), t('divination.future')];
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showNoReaskWarning, setShowNoReaskWarning] = useState(false);
  const [showClarifyInput, setShowClarifyInput] = useState(false);
  const [clarifyQuestion, setClarifyQuestion] = useState('');
  const [clarifyLoading, setClarifyLoading] = useState(false);
  const [followUpResult, setFollowUpResult] = useState(null);
  const clarifyInputRef = useRef(null);
  
  // Dynamic page title based on divination type
  const pageKey = result?.type === 'tarot' ? 'tarot' : result?.type === 'horoscope' ? 'horoscope' : 'numerology';
  usePageTitle(pageKey);
  
  // Focus clarify input when shown
  useEffect(() => {
    if (showClarifyInput && clarifyInputRef.current) {
      clarifyInputRef.current.focus();
    }
  }, [showClarifyInput]);

  // Handle inline clarify question submission
  const handleClarifySubmit = async () => {
    if (!clarifyQuestion.trim() || clarifyLoading) return;
    setClarifyLoading(true);
    try {
      const previousContext = result.interpretation || result.horoscope || '';
      const payload = {
        user_id: user?.id || null,
        topic: clarifyQuestion.trim(),
        previous_context: previousContext.substring(0, 1000),
        language: 'ru'
      };
      
      let response;
      if (result.type === 'tarot') {
        response = await axios.post(`${API}/divination/tarot`, payload);
      } else if (result.type === 'horoscope') {
        response = await axios.post(`${API}/divination/horoscope`, {
          ...payload,
          birth_date: result.birth_date || result.zodiac?.dates?.split(' - ')[0] || '01.01.1990',
          period: result.period || 'сегодня',
          user_date: new Date().toISOString().split('T')[0]
        });
      } else if (result.type === 'numerology') {
        response = await axios.post(`${API}/divination/numerology`, {
          ...payload,
          birth_date: result.birth_date || '01.01.1990'
        });
      }
      
      if (response?.data) {
        if (response.data.user) {
          // Update user credits in parent
        }
        setFollowUpResult(response.data);
        setShowClarifyInput(false);
        setClarifyQuestion('');
      }
    } catch (e) {
      void 0;
      if (e.response?.status === 402) {
        alert(t('app.insufficientCredits'));
      }
    } finally {
      setClarifyLoading(false);
    }
  };
  
  // Check if user needs to register to see full results
  const needsRegistration = !user;
  
  // Registration Wall Component
  const RegistrationWall = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="absolute inset-0 z-20 flex items-center justify-center"
    >
      {/* Blur overlay */}
      <div className="absolute inset-0 backdrop-blur-md bg-black/60" />
      
      {/* Registration prompt */}
      <div className="relative z-10 text-center px-6 max-w-md">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-[var(--card-bg)] border border-secondary/30 rounded-2xl p-8 shadow-2xl"
        >
          {/* Icon */}
          <div className="w-16 h-16 rounded-full bg-secondary/10 border border-secondary/30 
                          flex items-center justify-center mx-auto mb-4">
            <Eye size={32} className="text-secondary" />
          </div>
          
          {/* Title */}
          <h3 className="font-heading text-xl text-secondary tracking-widest uppercase mb-2">
            {t('auth.seeFullResult') || 'Раскройте послание'}
          </h3>
          
          {/* Subtitle */}
          <p className="text-muted-foreground text-sm mb-6">
            {t('auth.registerToSee') || 'Зарегистрируйтесь бесплатно, чтобы увидеть полную интерпретацию'}
          </p>
          
          {/* Bonus highlight */}
          <div className="bg-starlight/10 border border-starlight/30 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-starlight">
              <Gift size={20} />
              <span className="font-heading tracking-wider">
                {t('auth.freeCreditsBonus') || '100 бесплатных кредитов'}
              </span>
            </div>
            <p className="text-starlight/60 text-xs mt-1">
              {t('auth.creditsFor') || '= 10 гаданий бесплатно'}
            </p>
          </div>
          
          {/* Register button */}
          <button
            onClick={onShowAuth}
            className="w-full bg-secondary/10 border border-secondary/50 text-secondary
                       py-4 rounded-xl font-heading tracking-widest uppercase
                       hover:bg-secondary/20 transition-all active:scale-[0.98] mb-3"
            data-testid="register-wall-btn"
          >
            {t('auth.registerFree') || 'Регистрация бесплатно'}
          </button>
          
          {/* Login link */}
          <p className="text-muted-foreground text-sm">
            {t('auth.alreadyHaveAccount') || 'Уже есть аккаунт?'}{' '}
            <button 
              onClick={onShowAuth}
              className="text-secondary hover:underline"
            >
              {t('auth.loginButton') || 'Войти'}
            </button>
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
  
  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      <AnimatedBackground />
      
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[var(--card-bg)]/80 backdrop-blur-xl border-b border-[var(--glass-border)] px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="divination-back-btn"
          >
            <ArrowLeft size={20} />
            <span className="text-sm">{t('chat.back')}</span>
          </button>
          
          <span className="font-heading text-secondary tracking-widest uppercase text-sm">
            {result.type === 'tarot' && t('divination.tarotTitle')}
            {result.type === 'horoscope' && t('divination.horoscopeTitle')}
            {result.type === 'numerology' && t('divination.numerologyTitle')}
          </span>
          
          <div className="w-20" />
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        {/* Tarot Result */}
        {result.type === 'tarot' && (
          <div className="space-y-16">
            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <span className="text-starlight/60 text-sm tracking-[0.3em] uppercase">
                ✧ {t('divination.cardsReveal')} ✧
              </span>
            </motion.div>
            
            {/* Cards */}
            <div className="flex justify-center items-start gap-8 md:gap-16 flex-wrap">
              {result.cards.map((card, idx) => (
                <TarotCardDisplay 
                  key={idx} 
                  card={card} 
                  position={positions[idx]}
                  index={idx}
                />
              ))}
            </div>
            
            {/* Interpretation - with registration wall for non-users */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.5 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-starlight/5 to-transparent blur-xl" />
              <div className="relative bg-black/50 backdrop-blur-xl border border-starlight/20 rounded-2xl p-10">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <span className="text-starlight/40">✦</span>
                  <h3 className="font-heading text-xl text-starlight tracking-[0.2em] uppercase">
                    {t('divination.cardsMessage')}
                  </h3>
                  <span className="text-starlight/40">✦</span>
                </div>
                
                {/* Content with optional blur for non-registered users */}
                <div className={`relative ${needsRegistration ? 'min-h-[200px]' : ''}`}>
                  <p className={`text-foreground/90 leading-relaxed text-center text-lg italic max-w-3xl mx-auto ${
                    needsRegistration ? 'blur-md select-none' : ''
                  }`}>
                    {result.interpretation}
                  </p>
                  
                  {/* Registration wall overlay */}
                  {needsRegistration && <RegistrationWall />}
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Horoscope Result */}
        {result.type === 'horoscope' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="text-center">
              <motion.span 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.8 }}
                className="text-7xl mb-4 block filter drop-shadow-[0_0_30px_rgba(212,175,55,0.5)]"
              >
                {result.zodiac.symbol}
              </motion.span>
              <h2 className="font-heading text-3xl text-starlight tracking-widest uppercase">
                {result.zodiac.name_ru}
              </h2>
              <p className="text-muted-foreground mt-2">{result.zodiac.dates}</p>
            </div>
            
            <div className={`relative bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/20 rounded-2xl p-8 ${needsRegistration ? 'min-h-[200px]' : ''}`}>
              <h3 className="font-heading text-xl text-starlight tracking-widest uppercase mb-4 text-center">
                {t('divination.horoscopeFor')} {result.period}
              </h3>
              <p className={`text-foreground/90 leading-relaxed text-center italic ${
                needsRegistration ? 'blur-md select-none' : ''
              }`}>
                {result.horoscope}
              </p>
              
              {/* Registration wall overlay */}
              {needsRegistration && <RegistrationWall />}
            </div>
          </motion.div>
        )}

        {/* Numerology Result */}
        {result.type === 'numerology' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-[var(--card-bg)] backdrop-blur-xl border border-secondary/20 rounded-2xl p-6 text-center">
                <span className="text-4xl font-heading text-secondary">{result.numbers.life_path}</span>
                <p className="text-muted-foreground text-sm mt-2">{t('divination.lifePathNumber')}</p>
              </div>
              <div className="bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/20 rounded-2xl p-6 text-center">
                <span className="text-4xl font-heading text-starlight">{result.numbers.birthday_number}</span>
                <p className="text-muted-foreground text-sm mt-2">{t('divination.birthdayNumber')}</p>
              </div>
              <div className="bg-[var(--card-bg)] backdrop-blur-xl border border-accent/20 rounded-2xl p-6 text-center">
                <span className="text-4xl font-heading text-accent">{result.numbers.personal_year}</span>
                <p className="text-muted-foreground text-sm mt-2">{t('divination.personalYear')}</p>
              </div>
            </div>
            
            <div className={`relative bg-[var(--card-bg)] backdrop-blur-xl border border-secondary/20 rounded-2xl p-8 ${needsRegistration ? 'min-h-[200px]' : ''}`}>
              <h3 className="font-heading text-xl text-secondary tracking-widest uppercase mb-4 text-center">
                {t('divination.numbersMeaning')}
              </h3>
              <p className={`text-foreground/90 leading-relaxed text-center italic ${
                needsRegistration ? 'blur-md select-none' : ''
              }`}>
                {result.interpretation}
              </p>
              
              {/* Registration wall overlay */}
              {needsRegistration && <RegistrationWall />}
            </div>
          </motion.div>
        )}

        {/* What's Next Section */}
        {/* Follow-up Result Section */}
        {followUpResult && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-12 max-w-3xl mx-auto"
          >
            <div className="bg-starlight/5 border border-starlight/20 rounded-2xl p-8 relative">
              <div className="flex items-center gap-3 mb-6">
                <Sparkle size={24} className="text-starlight" />
                <h3 className="font-heading text-lg text-starlight tracking-widest uppercase">
                  {t('divination.followUpTitle') || 'Uточнение'}
                </h3>
              </div>

              {/* Follow-up Tarot Cards */}
              {followUpResult.type === 'tarot' && followUpResult.cards && (
                <div className="flex justify-center items-start gap-4 md:gap-8 flex-wrap mb-8">
                  {followUpResult.cards.map((card, idx) => (
                    <TarotCardDisplay 
                      key={`followup-${idx}`} 
                      card={card} 
                      position={positions[idx]}
                      index={idx}
                    />
                  ))}
                </div>
              )}

              <p className="text-foreground/90 leading-relaxed italic text-center">
                {followUpResult.interpretation || followUpResult.horoscope || ''}
              </p>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 3 }}
          className="mt-16 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <h3 className="font-heading text-lg text-muted-foreground tracking-widest uppercase">
              {t('divination.whatNext')}
            </h3>
          </div>

          {/* Warning message for re-asking */}
          {showNoReaskWarning && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 text-center"
            >
              <p className="text-amber-400/90 text-sm leading-relaxed">
                ⚠️ {t('divination.noReaskWarning')}
              </p>
            </motion.div>
          )}

          {/* Action Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {/* Clarifying Question */}
            {!showClarifyInput ? (
              <button
                onClick={() => setShowClarifyInput(true)}
                className="bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/20 rounded-xl p-6 text-left
                           hover:border-starlight/40 hover:bg-starlight/5 transition-all group"
                data-testid="clarify-question-btn"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Sparkle size={24} className="text-starlight group-hover:scale-110 transition-transform" />
                  <span className="font-heading text-sm text-starlight tracking-widest uppercase">
                    {t('divination.clarifyQuestion')}
                  </span>
                </div>
                <p className="text-muted-foreground text-xs">
                  {t('divination.clarifyHint')}
                </p>
              </button>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/40 rounded-xl p-6 sm:col-span-2"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Sparkle size={24} className="text-starlight" />
                  <span className="font-heading text-sm text-starlight tracking-widest uppercase">
                    {t('divination.clarifyQuestion')}
                  </span>
                </div>
                <div className="flex gap-3">
                  <input
                    ref={clarifyInputRef}
                    type="text"
                    value={clarifyQuestion}
                    onChange={(e) => setClarifyQuestion(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleClarifySubmit(); }}
                    placeholder={t('divination.clarifyPlaceholder') || 'Уточните ваш вопрос...'}
                    disabled={clarifyLoading}
                    className="flex-1 bg-[var(--card-bg)] border border-[var(--glass-border)] rounded-xl px-4 py-3 text-sm
                               text-foreground placeholder:text-muted-foreground focus:outline-none 
                               focus:border-starlight/60 disabled:opacity-50 transition-colors"
                    data-testid="clarify-question-input"
                  />
                  <button
                    onClick={handleClarifySubmit}
                    disabled={!clarifyQuestion.trim() || clarifyLoading}
                    className="px-5 py-3 bg-starlight/10 border border-starlight/50 text-starlight rounded-xl
                               font-heading text-sm tracking-wider uppercase
                               hover:bg-starlight/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all
                               flex items-center gap-2"
                    data-testid="clarify-submit-btn"
                  >
                    {clarifyLoading ? (
                      <SpinnerGap size={18} className="animate-spin" />
                    ) : (
                      <PaperPlaneTilt size={18} weight="fill" />
                    )}
                  </button>
                </div>
                <button 
                  onClick={() => { setShowClarifyInput(false); setClarifyQuestion(''); }}
                  className="mt-3 text-muted-foreground text-xs hover:text-foreground transition-colors"
                >
                  {t('chat.back') || 'Назад'}
                </button>
              </motion.div>
            )}

            {/* New Reading */}
            <button
              onClick={() => {
                if (onNewReading) onNewReading(result.type);
              }}
              className="bg-[var(--card-bg)] backdrop-blur-xl border border-secondary/20 rounded-xl p-6 text-left
                         hover:border-secondary/40 hover:bg-secondary/5 transition-all group"
              data-testid="new-reading-btn"
            >
              <div className="flex items-center gap-3 mb-2">
                <MoonStars size={24} className="text-secondary group-hover:scale-110 transition-transform" />
                <span className="font-heading text-sm text-secondary tracking-widest uppercase">
                  {t('divination.newReading')}
                </span>
              </div>
              <p className="text-muted-foreground text-xs">
                {t('divination.newReadingHint')}
              </p>
            </button>
          </div>

          {/* Secondary Actions */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {user && !emailSent && (
              <button
                onClick={async () => {
                  setEmailSending(true);
                  try {
                    await axios.post(`${API}/divination/send-email`, {
                      user_id: user.id,
                      result_type: result.type,
                      result_data: result
                    });
                    setEmailSent(true);
                  } catch (e) { void 0; }
                  finally { setEmailSending(false); }
                }}
                disabled={emailSending}
                className="bg-transparent border border-muted-foreground/20 text-muted-foreground
                           px-6 py-2 rounded-full text-xs tracking-wider uppercase
                           hover:border-muted-foreground/40 hover:text-foreground transition-all disabled:opacity-50 flex items-center gap-2"
                data-testid="divination-email-btn"
              >
                <EnvelopeSimple size={16} />
                {emailSending ? t('divination.sendingEmail') : t('divination.sendToEmail')}
              </button>
            )}
            {emailSent && (
              <span className="text-green-400 text-xs">{t('divination.sentToEmail')}</span>
            )}
            <button
              onClick={() => {
                const shareText = result.type === 'tarot' 
                  ? `ONIMA Tarot: ${result.cards?.map(c => c.name).join(', ') || ''}` 
                  : result.type === 'horoscope' 
                  ? `ONIMA Horoscope: ${result.zodiac_sign || ''}`
                  : `ONIMA Numerology: Life Path ${result.numbers?.life_path || ''}`;
                const shareUrl = 'https://onima.cc';
                if (navigator.share) {
                  navigator.share({ title: 'ONIMA', text: shareText, url: shareUrl }).catch(() => {});
                } else {
                  navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
                }
              }}
              className="bg-transparent border border-muted-foreground/20 text-muted-foreground
                         px-6 py-2 rounded-full text-xs tracking-wider uppercase
                         hover:border-muted-foreground/40 hover:text-foreground transition-all flex items-center gap-2"
              data-testid="share-result-btn"
            >
              <ShareNetwork size={16} />
              {t('divination.shareResult') || 'Share'}
            </button>
            <button
              onClick={onBack}
              className="bg-transparent border border-muted-foreground/20 text-muted-foreground
                         px-6 py-2 rounded-full text-xs tracking-wider uppercase
                         hover:border-muted-foreground/40 hover:text-foreground transition-all flex items-center gap-2"
              data-testid="back-home-btn"
            >
              <ArrowLeft size={16} />
              {t('chat.back')}
            </button>
          </div>
        </motion.div>
        
        {/* Legal disclaimer */}
        <DivinationDisclaimer />
      </main>
    </div>
  );
};

// Home Page
const Home = ({ onSelectTopic, onSelectDivination, onStartInlineChat, onStartAskezaChat, user, language, onShowAuth }) => {
  const { t } = useLanguage();
  usePageTitle('home');
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [topics, setTopics] = useState([]);
  const [divinationMethods, setDivinationMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [inlineResponse, setInlineResponse] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [topicsRes, divinationRes] = await Promise.all([
          axios.get(`${API}/topics`),
          axios.get(`${API}/divination/methods`)
        ]);
        setTopics(topicsRes.data);
        setDivinationMethods(divinationRes.data);
      } catch (e) {
        void 0;
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSendChat = async (overrideText) => {
    const msg = (typeof overrideText === 'string' ? overrideText : chatInput || '').trim();
    if (!msg || chatLoading) return;
    setChatInput(''); // Clear input immediately
    setChatLoading(true);
    try {
      const res = await axios.post(`${API}/chat/route`, {
        message: msg,
        user_id: user?.id || null,
        language: language || 'ru'
      });
      setInlineResponse({
        userMessage: msg,
        oracleResponse: res.data.oracle_response,
        sessionId: res.data.session_id,
        topicId: res.data.topic_id,
        topic: res.data.topic,
        stage: res.data.stage,
        quickReplies: res.data.quick_replies,
        user: res.data.user,
        askezaSuggestion: res.data.askeza_suggestion,
      });
    } catch (err) {
      void 0;
      if (err.response?.status === 403 && err.response?.data?.detail === 'anonymous_limit_reached') {
        if (onShowAuth) onShowAuth();
      }
    } finally {
      setChatLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendChat();
    }
  };

  const handleContinueChat = () => {
    if (inlineResponse) {
      onStartInlineChat(inlineResponse);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <AnimatedBackground />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="pt-12 pb-6 text-center px-6"
        >
          <button 
            onClick={() => { setInlineResponse(null); setChatInput(''); }}
            className="inline-block cursor-pointer hover:opacity-80 transition-opacity"
            data-testid="onima-logo-home"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <MoonStars size={36} weight="duotone" className="text-starlight animate-breathe" />
              <Eye size={44} weight="duotone" className="text-ethereal animate-breathe" style={{ animationDelay: '1s' }} />
              <MoonStars size={36} weight="duotone" className="text-starlight animate-breathe" style={{ animationDelay: '2s' }} />
            </div>
            <h1 className={`font-heading text-4xl md:text-5xl tracking-[0.3em] uppercase text-transparent bg-clip-text ${isLight ? 'bg-gradient-to-r from-amber-700 to-amber-500 drop-shadow-[0_2px_4px_rgba(255,255,255,0.9)]' : 'bg-gradient-to-r from-starlight to-amber-200 drop-shadow-[0_0_30px_rgba(212,175,55,0.3)]'}`}>
              {t('app.title')}
            </h1>
          </button>
          <p className={`mt-3 text-base max-w-xl mx-auto leading-relaxed ${isLight ? 'text-gray-800 drop-shadow-[0_1px_2px_rgba(255,255,255,0.9)]' : 'text-muted-foreground'}`}>
            {t('app.subtitle')}
          </p>
        </motion.header>

        {/* Main Content */}
        <main className="flex-1 px-6 md:px-12 py-6 max-w-7xl mx-auto w-full">
          {loading ? (
            <div className="flex justify-center py-20">
              <SpinnerGap size={48} className="text-starlight animate-spin" />
            </div>
          ) : (
            <>
              {/* Inline Chat Area */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.5 }}
                className="max-w-2xl mx-auto mb-10"
              >
                <AnimatePresence mode="wait">
                  {!inlineResponse ? (
                    <motion.div
                      key="input"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center gap-3 bg-[var(--card-bg)] backdrop-blur-xl 
                                 border border-[var(--glass-border)] rounded-2xl px-5 py-3
                                 focus-within:border-starlight/40 focus-within:shadow-[0_0_30px_rgba(212,175,55,0.15)]
                                 transition-all duration-300"
                      data-testid="home-chat-input-area"
                    >
                      <ChatCircleDots size={22} weight="duotone" className="text-starlight/50 shrink-0" />
                      {user && (
                        <VoiceRecordButton
                          user={user}
                          onTranscription={(text) => {
                            setChatInput(text);
                            setTimeout(() => handleSendChat(text), 100);
                          }}
                          disabled={chatLoading}
                          language={language}
                        />
                      )}
                      <input
                        ref={inputRef}
                        type="text"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t('home.chatPrompt')}
                        disabled={chatLoading}
                        className="flex-1 bg-transparent text-foreground text-sm placeholder:text-muted-foreground
                                   focus:outline-none disabled:opacity-50"
                        data-testid="home-chat-input"
                      />
                      <button
                        onClick={handleSendChat}
                        disabled={!chatInput.trim() || chatLoading}
                        className="w-9 h-9 rounded-xl bg-starlight/10 border border-starlight/30 
                                   flex items-center justify-center text-starlight shrink-0
                                   hover:bg-starlight/20 disabled:opacity-30 disabled:cursor-not-allowed
                                   transition-all active:scale-95"
                        data-testid="home-chat-send"
                      >
                        {chatLoading ? (
                          <SpinnerGap size={16} className="animate-spin" />
                        ) : (
                          <PaperPlaneTilt size={16} weight="fill" />
                        )}
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="response"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-[var(--card-bg)] backdrop-blur-xl border border-starlight/20 
                                 rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(212,175,55,0.1)]"
                      data-testid="home-inline-chat"
                    >
                      {/* User message */}
                      <div className="px-5 pt-4 pb-2">
                        <div className="flex justify-end">
                          <div className="bg-starlight/10 border border-starlight/20 rounded-xl rounded-tr-sm px-4 py-2.5 max-w-[85%]">
                            <p className="text-foreground text-sm">{inlineResponse.userMessage}</p>
                          </div>
                        </div>
                      </div>
                      {/* Oracle response */}
                      <div className="px-5 pb-2">
                        <div className="flex justify-start">
                          <div className="bg-secondary/5 border border-secondary/15 rounded-xl rounded-tl-sm px-4 py-3 max-w-[90%]">
                            <p className="text-foreground/85 text-sm leading-relaxed whitespace-pre-line">{inlineResponse.oracleResponse}</p>
                            {user?.voice_enabled && (
                              <div className="mt-2 flex justify-end">
                                <TTSPlayButton text={inlineResponse.oracleResponse} user={user} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Continue button */}
                      <div className="px-5 py-4 border-t border-[var(--glass-border)]">
                        <button
                          onClick={handleContinueChat}
                          className="w-full bg-starlight/10 border border-starlight/40 text-starlight
                                     py-3 rounded-xl font-heading text-sm tracking-widest uppercase
                                     hover:bg-starlight/20 hover:shadow-[0_0_20px_rgba(212,175,55,0.2)]
                                     transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                          data-testid="home-continue-chat"
                        >
                          <ChatCircleDots size={18} weight="duotone" />
                          {t('home.continueChat')}
                          <ArrowRight size={16} />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Divination Section */}
              <section className="mb-12">
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className={`text-center mb-6 font-heading tracking-widest uppercase text-sm ${isLight ? 'text-gray-800 drop-shadow-[0_1px_2px_rgba(255,255,255,0.9)]' : 'text-secondary/80'}`}
                >
                  {t('home.divinationTitle')}
                </motion.p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                  {divinationMethods.map((method, index) => (
                    <DivinationCard 
                      key={method.id} 
                      method={method} 
                      index={index}
                      onClick={() => onSelectDivination(method)}
                    />
                  ))}
                </div>
              </section>

              {/* Askeza AI Section — prominent CTA after divination */}
              <section className="mt-10 mb-10">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="max-w-3xl mx-auto"
                >
                  <button
                    onClick={onStartAskezaChat}
                    className="w-full group relative overflow-hidden rounded-2xl 
                               bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10
                               border border-amber-500/25 p-5 md:p-7
                               hover:border-amber-500/50 hover:shadow-[0_0_40px_rgba(245,158,11,0.15)]
                               transition-all duration-500 text-left"
                    data-testid="home-askeza-ai-btn"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-amber-500/15 border border-amber-500/30
                                      flex items-center justify-center shrink-0
                                      group-hover:bg-amber-500/25 transition-colors">
                        <Fire size={26} weight="duotone" className="text-amber-500" />
                      </div>
                      <div className="min-w-0">
                        <h3 className={`font-heading tracking-[0.15em] uppercase text-sm md:text-base mb-0.5 ${isLight ? 'text-amber-800' : 'text-amber-400'}`}>
                          {t('askezaChat.homeTitle')}
                        </h3>
                        <p className="text-xs md:text-sm text-muted-foreground leading-relaxed truncate">
                          {t('askezaChat.homeSubtitle')}
                        </p>
                      </div>
                      <ArrowRight size={22} className="text-amber-500/50 group-hover:text-amber-500 transition-colors shrink-0 ml-auto" />
                    </div>
                  </button>
                </motion.div>
              </section>

              {/* Topics Section — compact horizontal chips */}
              <section className="mb-8">
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className={`text-center mb-5 font-heading tracking-widest uppercase text-sm ${isLight ? 'text-gray-800 drop-shadow-[0_1px_2px_rgba(255,255,255,0.9)]' : 'text-starlight/80'}`}
                >
                  {t('home.oracleTitle')}
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45 }}
                  className="flex flex-wrap justify-center gap-2.5 max-w-3xl mx-auto"
                >
                  {topics.map((topic, index) => {
                    const ChipIcon = ICON_MAP[topic.icon] || Sparkle;
                    return (
                    <motion.button
                      key={topic.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45 + index * 0.05 }}
                      onClick={() => onSelectTopic(topic)}
                      className={`group flex items-center gap-2 px-4 py-2.5 rounded-full
                                 border transition-all duration-300
                                 ${isLight 
                                   ? 'bg-white/60 border-gray-200 hover:border-amber-400 hover:bg-amber-50/60 text-gray-700 hover:text-amber-800' 
                                   : 'bg-[var(--card-bg)] border-[var(--glass-border)] hover:border-starlight/40 hover:bg-starlight/5 text-foreground/80 hover:text-starlight'
                                 }`}
                      data-testid={`topic-chip-${topic.id}`}
                    >
                      <ChipIcon size={16} weight="duotone" className="opacity-70 group-hover:opacity-100 transition-opacity" />
                      <span className="font-heading tracking-wider uppercase text-xs">
                        {t(`topics.${topic.id}`) || topic.name_ru || topic.name}
                      </span>
                    </motion.button>
                    );
                  })}
                </motion.div>
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// Chat Message Component
const ChatMessage = ({ message, isOracle, user }) => {
  const { t } = useLanguage();
  const isDivinationResult = message.type === 'divination_result';
  const isDivinationLoading = message.type === 'divination_loading';
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  const handleSendEmail = async () => {
    if (!user) return;
    setEmailSending(true);
    try {
      await axios.post(`${API}/divination/send-email`, {
        user_id: user.id,
        result_type: message.divination_type,
        result_data: { interpretation: message.content, type: message.divination_type }
      });
      setEmailSent(true);
    } catch (e) { void 0; }
    finally { setEmailSending(false); }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`flex ${isOracle ? 'justify-start' : 'justify-end'} mb-6`}
    >
      <div className={`max-w-[80%] ${isOracle ? 'text-left' : 'text-right'}`}>
        {isOracle && (
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-starlight/30 animate-breathe">
              <img 
                src="https://images.pexels.com/photos/1774954/pexels-photo-1774954.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="ONIMA"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="font-heading text-starlight text-sm tracking-widest uppercase">{t('chat.oracle')}</span>
            {isDivinationResult && (
              <span className="text-xs text-ethereal/70 bg-ethereal/10 px-2 py-0.5 rounded-full">
                {message.divination_type === 'tarot' ? t('chat.divinationTarot') : 
                 message.divination_type === 'horoscope' ? t('chat.divinationHoroscope') : t('chat.divinationNumerology')}
              </span>
            )}
          </div>
        )}
        <div 
          className={`
            ${isDivinationResult
              ? 'bg-[var(--card-bg)] border border-starlight/30 rounded-2xl px-5 py-4 shadow-[0_0_20px_rgba(212,175,55,0.08)]'
              : isDivinationLoading
              ? 'bg-[var(--card-bg)] border border-ethereal/20 rounded-2xl px-5 py-3'
              : isOracle 
              ? 'bg-[var(--card-bg)] border-l-2 border-starlight/50 pl-4 pr-6 py-3' 
              : 'bg-ethereal/20 border border-ethereal/30 px-5 py-3 rounded-2xl rounded-br-sm'
            }
          `}
          data-testid={isDivinationResult ? "divination-result-message" : isOracle ? "oracle-message" : "user-message"}
        >
          {isDivinationLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <SpinnerGap size={20} className="animate-spin text-starlight" />
              <span className="text-sm italic">{message.content}</span>
            </div>
          ) : (
            <p className={`leading-relaxed whitespace-pre-wrap ${
              isDivinationResult ? 'text-foreground/95' : 
              isOracle ? 'text-foreground/90 italic' : 'text-foreground'
            }`}>
              {message.content}
            </p>
          )}
          {/* Email button for in-chat divination results */}
          {isDivinationResult && user && (
            <div className="mt-3 pt-3 border-t border-starlight/10 flex items-center gap-3">
              {!emailSent ? (
                <button
                  onClick={handleSendEmail}
                  disabled={emailSending}
                  className="flex items-center gap-1.5 text-xs text-starlight/60 hover:text-starlight transition-colors disabled:opacity-50"
                  data-testid="chat-divination-email-btn"
                >
                  <EnvelopeSimple size={14} />
                  {emailSending ? t('divination.sendingEmail') : t('divination.sendToEmail')}
                </button>
              ) : (
                <span className="text-xs text-green-400">{t('divination.sentToEmail')}</span>
              )}
            </div>
          )}
          {/* TTS play button for oracle messages */}
          {isOracle && !isDivinationLoading && user && (
            <div className="mt-2 flex justify-end">
              <TTSPlayButton text={message.content} user={user} />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Divination Offer Card (in-chat upsell)
const DivinationOfferCard = ({ offers, onSelect, language, isLoading }) => {
  const { t } = useLanguage();
  const typeIcons = { tarot: '&#9827;', horoscope: '&#9734;', numerology: '#' };
  const typeNames = { 
    tarot: t('chat.divinationTarot'), 
    horoscope: t('chat.divinationHoroscope'), 
    numerology: t('chat.divinationNumerology') 
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex justify-start mb-6"
      data-testid="divination-offer-container"
    >
      <div className="max-w-[85%]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-starlight/30 animate-breathe">
            <img 
              src="https://images.pexels.com/photos/1774954/pexels-photo-1774954.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="ONIMA"
              className="w-full h-full object-cover"
            />
          </div>
          <span className="font-heading text-starlight text-sm tracking-widest uppercase">{t('chat.oracle')}</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {offers.map((offer, idx) => {
            const textKey = language === 'uk' ? 'text_uk' : language === 'ru' ? 'text_ru' : 'text_en';
            return (
              <motion.button
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.15 }}
                onClick={() => onSelect(offer)}
                disabled={isLoading}
                className="bg-[var(--card-bg)] border border-starlight/30 rounded-2xl p-4 text-left
                           hover:border-starlight/60 hover:shadow-[0_0_20px_rgba(212,175,55,0.15)]
                           transition-all duration-300 active:scale-95 disabled:opacity-50
                           min-w-[200px] max-w-[280px]"
                data-testid={`divination-offer-${offer.type}`}
              >
                <p className="text-foreground/80 text-sm mb-3 leading-relaxed">
                  {offer[textKey] || offer.text_ru}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-starlight font-heading text-xs tracking-wider uppercase">
                    {typeNames[offer.type] || offer.type}
                  </span>
                  <span className="bg-starlight/10 border border-starlight/30 text-starlight 
                                   px-3 py-1 rounded-full text-xs font-medium">
                    {t('chat.divinationAccept')} · 10 {t('chat.divinationCost')}
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

// Email Collection Modal
const EmailModal = ({ onSubmit, onClose }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    await onSubmit(email);
    setIsSubmitting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999999] flex items-center justify-center p-6"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-void-blue/90 backdrop-blur-xl border border-starlight/30 rounded-2xl p-8 max-w-md w-full shadow-[0_0_60px_rgba(212,175,55,0.2)]"
        data-testid="email-modal"
      >
        <div className="text-center mb-8">
          <EnvelopeSimple size={48} weight="duotone" className="text-starlight mx-auto mb-4 animate-float" />
          <h2 className="font-heading text-2xl tracking-widest uppercase text-starlight mb-3">
            {t('email.saveConnection')}
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            {t('email.saveConnectionDesc')}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full bg-transparent border-b-2 border-[var(--glass-border)] px-0 py-3 
                         text-lg text-foreground placeholder:text-muted-foreground
                         focus:border-starlight focus:outline-none transition-colors"
              data-testid="email-input"
              required
            />
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting || !email}
            className="w-full bg-starlight/10 border border-starlight/50 text-starlight
                       py-4 rounded-xl font-heading tracking-widest uppercase
                       hover:bg-starlight/20 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-300 active:scale-95"
            data-testid="email-submit-btn"
          >
            {isSubmitting ? (
              <SpinnerGap size={24} className="animate-spin mx-auto" />
            ) : (
              t('email.getPrediction')
            )}
          </button>
        </form>
        
        <button
          onClick={onClose}
          className="mt-4 w-full text-muted-foreground text-sm hover:text-foreground transition-colors"
          data-testid="email-skip-btn"
        >
          {t('email.skipEmail')}
        </button>
      </motion.div>
    </motion.div>
  );
};

// Thank You Screen
const ThankYouScreen = ({ onNewSession }) => {
  const { t } = useLanguage();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex flex-col items-center justify-center py-20 px-6 text-center"
    >
      <Sparkle size={64} weight="duotone" className="text-starlight mb-6 animate-glow-pulse" />
      <h2 className="font-heading text-3xl tracking-widest uppercase text-starlight mb-4">
        {t('thankYou.title')}
      </h2>
      <p className="text-muted-foreground max-w-md mb-8 leading-relaxed">
        {t('thankYou.desc')}
      </p>
      <button
        onClick={onNewSession}
        className="bg-transparent border border-starlight/30 text-starlight
                   px-8 py-3 rounded-full font-heading tracking-widest uppercase text-sm
                   hover:bg-starlight/10 hover:border-starlight/50
                   transition-all duration-300"
        data-testid="new-session-btn"
      >
        {t('chat.startNewPath')}
      </button>
    </motion.div>
  );
};

// Registration Gate Modal — shown when anonymous user reaches protocol stage
const RegistrationGateModal = ({ onShowAuth, onClose }) => {
  const { t } = useLanguage();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[999999] flex items-center justify-center p-6"
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="relative bg-void-blue/95 backdrop-blur-xl border border-starlight/30 rounded-2xl p-8 max-w-md w-full shadow-[0_0_60px_rgba(212,175,55,0.25)]"
        data-testid="registration-gate-modal"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-starlight/10 border border-starlight/30 mx-auto mb-4 flex items-center justify-center">
            <Crown size={32} weight="duotone" className="text-starlight" />
          </div>
          <h2 className="font-heading text-2xl tracking-widest uppercase text-starlight mb-3">
            {t('gate.title') || 'Твой план готов'}
          </h2>
          <p className="text-muted-foreground leading-relaxed text-sm">
            {t('gate.description') || 'Оракул подготовил персональный план действий на основе вашего диалога. Зарегистрируйтесь бесплатно, чтобы получить его полностью.'}
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 text-sm text-foreground/70">
            <Check size={18} className="text-starlight flex-shrink-0" />
            <span>{t('gate.benefit1') || 'Полный план действий на email'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-foreground/70">
            <Check size={18} className="text-starlight flex-shrink-0" />
            <span>{t('gate.benefit2') || 'Сохранение истории диалогов'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-foreground/70">
            <Check size={18} className="text-starlight flex-shrink-0" />
            <span>{t('gate.benefit3') || 'Бесплатные кредиты для гаданий'}</span>
          </div>
        </div>

        <button
          onClick={() => { onClose(); onShowAuth(); }}
          className="w-full bg-starlight/15 border border-starlight/50 text-starlight
                     py-4 rounded-xl font-heading tracking-widest uppercase
                     hover:bg-starlight/25 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]
                     transition-all duration-300 active:scale-95"
          data-testid="gate-register-btn"
        >
          {t('gate.registerBtn') || 'Зарегистрироваться бесплатно'}
        </button>

        <button
          onClick={onClose}
          className="mt-3 w-full text-muted-foreground/50 text-xs hover:text-muted-foreground transition-colors"
          data-testid="gate-close-btn"
        >
          {t('gate.later') || 'Позже'}
        </button>
      </motion.div>
    </motion.div>
  );
};

// Chat Page
const GUEST_SESSION_KEY = 'onima_guest_session';

const ChatPage = ({ topic, onBack, user, onUserUpdate, savedProfile, onShowAuth, routedSession }) => {
  const { t, language } = useLanguage();
  usePageTitle('chat');
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailCollected, setEmailCollected] = useState(false);
  const [trustScore, setTrustScore] = useState(0);
  const [exchangeCount, setExchangeCount] = useState(0);
  const [chatPhase, setChatPhase] = useState('talk');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showRegistrationGate, setShowRegistrationGate] = useState(false);
  const [sessionStartTime] = useState(Date.now());
  const [protocolRequested, setProtocolRequested] = useState(false);
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const [quickReplies, setQuickReplies] = useState([]);

  // Divination offer state (in-chat upsell)
  const [divinationOffer, setDivinationOffer] = useState(null);
  const [askezaSuggestion, setAskezaSuggestion] = useState(null);
  const [divinationLoading, setDivinationLoading] = useState(false);
  const [showDatePickerForDiv, setShowDatePickerForDiv] = useState(null); // 'horoscope' or 'numerology'

  // === GUEST SESSION PERSISTENCE ===
  // Save session to localStorage for anonymous users
  const saveGuestSession = useCallback(() => {
    if (!user && sessionId && messages.length > 0) {
      const sessionData = {
        sessionId,
        topicId: topic.id,
        messages,
        trustScore,
        exchangeCount,
        chatPhase,
        savedAt: Date.now()
      };
      localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(sessionData));
    }
  }, [user, sessionId, messages, trustScore, exchangeCount, chatPhase, topic.id]);

  // Clear guest session (after registration)
  const clearGuestSession = () => {
    localStorage.removeItem(GUEST_SESSION_KEY);
  };

  // Save on every message change
  useEffect(() => {
    saveGuestSession();
  }, [messages, saveGuestSession]);

  // Clear guest session when user logs in
  useEffect(() => {
    if (user) {
      clearGuestSession();
    }
  }, [user]);

  // Check if summarize button should show
  const shouldShowSummarizeButton = () => {
    if (protocolRequested || emailSent) return false;
    if (chatPhase === 'protocol' || chatPhase === 'summary') return false;
    const minutesPassed = (Date.now() - sessionStartTime) / 60000;
    const userMessages = messages.filter(m => m.role === 'user').length;
    return userMessages >= 5 || minutesPassed >= 3; // Show after 5 messages or 3 minutes
  };

  // Get initial quick replies from translations
  const getInitialQuickReplies = () => {
    const replies = t(`quickReplies.${topic.id}`);
    return Array.isArray(replies) ? replies : t('quickReplies.growth');
  };

  const getCurrentQuickReplies = () => {
    if (isLoading) return [];
    // Use dynamic replies from backend if available (priority)
    if (quickReplies.length > 0) return quickReplies;
    // Fallback to initial replies only for very first interaction (before any message sent)
    if (messages.length <= 1) {
      return getInitialQuickReplies();
    }
    return [];
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Track if session has been initialized (prevent re-initialization on user change)
  const sessionInitializedRef = useRef(false);

  // Start session - only once on mount or topic change
  useEffect(() => {
    // Skip if session already initialized
    if (sessionInitializedRef.current && sessionId) {
      return;
    }

    const startSession = async () => {
      try {
        // Check for pre-loaded session from inline chat on homepage
        if (routedSession) {
          setSessionId(routedSession.sessionId);
          const initialMsgs = [];
          if (routedSession.userMessage) {
            initialMsgs.push({ role: 'user', content: routedSession.userMessage });
          }
          if (routedSession.oracleResponse) {
            initialMsgs.push({ role: 'oracle', content: routedSession.oracleResponse });
          }
          setMessages(initialMsgs);
          setExchangeCount(1);
          setChatPhase(routedSession.stage || 'orientation');
          if (routedSession.quickReplies) setQuickReplies(routedSession.quickReplies);
          if (routedSession.askezaSuggestion) setAskezaSuggestion(routedSession.askezaSuggestion);
          trackEvent('chat_session_from_inline', user?.id, { topic: topic.id });
          sessionInitializedRef.current = true;
          setIsStarting(false);
          return;
        }

        // Check for saved guest session (only for non-logged-in users)
        if (!user) {
          const saved = localStorage.getItem(GUEST_SESSION_KEY);
          if (saved) {
            try {
              const data = JSON.parse(saved);
              const hoursSinceSave = (Date.now() - data.savedAt) / (1000 * 60 * 60);
              if (data.topicId === topic.id && hoursSinceSave < 24) {
                setSessionId(data.sessionId);
                setMessages(data.messages);
                setTrustScore(data.trustScore || 0);
                setExchangeCount(data.exchangeCount || 0);
                setChatPhase(data.chatPhase || 'talk');
                trackEvent('chat_session_restored', null, { topic: topic.id });
                sessionInitializedRef.current = true;
                setIsStarting(false);
                return;
              }
            } catch (e) {
              void 0;
            }
          }
        }

        // Start new session
        const response = await axios.post(`${API}/sessions/start`, {
          topic_id: topic.id,
          language: language,
          user_id: user?.id || null
        });
        setSessionId(response.data.session_id);
        trackEvent('chat_session_started', user?.id, { topic: topic.id });
        setMessages([{
          role: 'oracle',
          content: response.data.initial_message
        }]);
        sessionInitializedRef.current = true;
      } catch (e) {
        void 0;
      } finally {
        setIsStarting(false);
      }
    };
    startSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic.id, language, routedSession]); // Removed loadGuestSession - causes re-init on user change

  const sendMessage = async (e, quickReply = null, forceProtocol = false) => {
    if (e) e.preventDefault();
    const messageText = quickReply || inputValue.trim();
    if (!messageText || isLoading || !sessionId) return;

    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: messageText }]);
    setIsLoading(true);
    setExchangeCount(prev => prev + 1);
    setQuickReplies([]); // Clear current replies while loading

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: messageText,
        user_id: user?.id || null,
        language: language,
        force_protocol: forceProtocol
      });

      setMessages(prev => [...prev, { 
        role: 'oracle', 
        content: response.data.response 
      }]);
      setTrustScore(response.data.trust_score);
      setExchangeCount(prev => prev + 1);
      
      // Update quick replies from backend
      if (response.data.quick_replies && Array.isArray(response.data.quick_replies)) {
        setQuickReplies(response.data.quick_replies);
      }

      // Capture divination offer from backend
      if (response.data.divination_offer) {
        setDivinationOffer(response.data.divination_offer);
      }

      // Capture askeza suggestion from backend
      if (response.data.askeza_suggestion) {
        setAskezaSuggestion(response.data.askeza_suggestion);
      } else {
        setAskezaSuggestion(null);
      }
      
      // Track phase/stage
      if (response.data.stage) {
        setChatPhase(response.data.stage);
      } else if (response.data.phase) {
        setChatPhase(response.data.phase);
      }

      // Update user credits
      if (response.data.user && onUserUpdate) {
        onUserUpdate(response.data.user);
      }

      // Auto-send protocol to email and redirect to home
      if (response.data.stage === 'protocol' && user?.email) {
        try {
          setEmailSending(true);
          await axios.post(`${API}/chat/send-summary`, { session_id: sessionId, user_id: user.id });
          setEmailSent(true);
          setMessages(prev => [...prev, { 
            role: 'oracle', 
            content: t('chat.planSentSuccess')
          }]);
          // Redirect to home after a delay
          setTimeout(() => onBack(), 4000);
        } catch (emailErr) {
          void 0;
        } finally {
          setEmailSending(false);
        }
      }

      // Registration gate for non-authenticated users (e.g., protocol stage)
      if (!user && response.data.should_collect_email && !emailCollected) {
        setShowRegistrationGate(true);
      }
    } catch (e) {
      void 0;
      if (e.response?.status === 403 && e.response?.data?.detail === 'anonymous_limit_reached') {
        setMessages(prev => [...prev, { 
          role: 'oracle', 
          content: t('chat.anonymousLimitReached') || 'You have reached the free action limit. Please register to continue.'
        }]);
        setShowRegistrationGate(true);
      } else if (e.response?.status === 402) {
        setMessages(prev => [...prev, { 
          role: 'oracle', 
          content: t('chat.outOfCredits')
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'oracle', 
          content: t('chat.connectionLost')
        }]);
      }
    } finally {
      setIsLoading(false);
      setTimeout(() => chatInputRef.current?.focus(), 50);
    }
  };

  const handleEmailSubmit = async (email) => {
    try {
      await axios.post(`${API}/email/submit`, {
        session_id: sessionId,
        email: email
      });
      setEmailCollected(true);
      setShowEmailModal(false);
    } catch (e) {
      void 0;
    }
  };

  // Handle divination from chat offer
  const handleChatDivination = async (divType, birthDate = null) => {
    if (!user || divinationLoading) return;
    
    setDivinationLoading(true);
    setDivinationOffer(null);
    setShowDatePickerForDiv(null);
    
    // Add a "running divination" message
    setMessages(prev => [...prev, { 
      role: 'oracle', 
      content: t('chat.divinationRunning'),
      type: 'divination_loading'
    }]);
    
    try {
      const response = await axios.post(`${API}/chat/divination`, {
        session_id: sessionId,
        user_id: user.id,
        divination_type: divType,
        birth_date: birthDate,
        language: language
      });
      
      // Replace loading message with actual result
      setMessages(prev => {
        const filtered = prev.filter(m => m.type !== 'divination_loading');
        return [...filtered, {
          role: 'oracle',
          content: response.data.interpretation,
          type: 'divination_result',
          divination_type: divType
        }];
      });
      
      // Update user credits
      if (response.data.user && onUserUpdate) {
        onUserUpdate(response.data.user);
      }
    } catch (e) {
      void 0;
      setMessages(prev => {
        const filtered = prev.filter(m => m.type !== 'divination_loading');
        return [...filtered, {
          role: 'oracle',
          content: e.response?.status === 402 ? t('chat.outOfCredits') : 'Что-то пошло не так... Давай вернёмся к разговору.'
        }];
      });
    } finally {
      setDivinationLoading(false);
    }
  };

  const handleDivOfferClick = (offer) => {
    if (offer.type === 'tarot') {
      // Tarot doesn't need birth date
      handleChatDivination('tarot');
    } else {
      // Horoscope and numerology need birth date
      // Check if user has saved birth date
      if (savedProfile?.birth_date) {
        handleChatDivination(offer.type, savedProfile.birth_date);
      } else {
        setShowDatePickerForDiv(offer.type);
      }
    }
  };

  const IconComponent = ICON_MAP[topic.icon] || Sparkle;

  if (emailCollected) {
    return (
      <div className="flex-1 bg-background relative">
        <AnimatedBackground />
        <div className="relative z-10">
          <ThankYouScreen onNewSession={onBack} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-background flex flex-col relative overflow-hidden">
      <AnimatedBackground />
      
      {/* Header - sticky so back button is always accessible */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-20 bg-[var(--card-bg)]/80 backdrop-blur-xl border-b border-[var(--glass-border)] px-6 py-4"
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
            <span className="font-heading text-starlight tracking-[0.15em] text-xs uppercase">ONIMA</span>
          </button>
          
          <div className="flex items-center gap-3">
            <IconComponent size={24} weight="duotone" className="text-starlight" />
            <span className="font-heading text-starlight tracking-widest uppercase text-sm">
              {t(`topics.${topic.id}`) || topic.name_ru}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {user && (
              <button 
                onClick={() => onShowAuth && onShowAuth()}
                className="flex items-center gap-1.5 text-xs text-starlight/60 hover:text-starlight transition-colors"
                data-testid="chat-credits-display"
              >
                <MarioCoin size={14} />
                <span>{user.total_credits}</span>
              </button>
            )}
          </div>
        </div>
      </motion.header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto px-6 py-8 relative z-10">
        <div className="max-w-3xl mx-auto">
          {isStarting ? (
            <div className="flex justify-center py-20">
              <SpinnerGap size={48} className="text-starlight animate-spin" />
            </div>
          ) : (
            <>
              {messages.map((message, index) => (
                <ChatMessage 
                  key={index} 
                  message={message} 
                  isOracle={message.role === 'oracle'}
                  user={user}
                />
              ))}
              {/* Divination Offer Cards */}
              {divinationOffer && !divinationLoading && (
                <DivinationOfferCard 
                  offers={divinationOffer}
                  onSelect={handleDivOfferClick}
                  language={language}
                  isLoading={divinationLoading}
                />
              )}
              {/* Askeza suggestion buttons */}
              {askezaSuggestion && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-wrap gap-2 mt-3"
                  data-testid="askeza-suggestion-buttons"
                >
                  <button
                    onClick={async () => {
                      if (!user) {
                        setShowRegistrationGate(true);
                        return;
                      }
                      try {
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        await axios.post(`${API}/askezes/`, {
                          user_id: user.id,
                          type: askezaSuggestion.type || 'other',
                          description: askezaSuggestion.name,
                          goal: askezaSuggestion.goal || '',
                          duration: askezaSuggestion.duration || 21,
                          start_date: tomorrow.toISOString().split('T')[0],
                          health_warning: false
                        });
                        setAskezaSuggestion(null);
                        // Send confirmation message
                        const confirmMsgs = {
                          ru: "Записала! Аскеза начинается завтра. Я буду рядом — проверю как дела.",
                          en: "Got it! Your askeza starts tomorrow. I'll be here — I'll check on you.",
                          uk: "Записала! Аскеза починається завтра. Я буду поруч — перевірю як справи.",
                          de: "Notiert! Deine Askese beginnt morgen. Ich bin da — ich schaue nach dir."
                        };
                        setMessages(prev => [...prev, { 
                          role: 'oracle', 
                          content: confirmMsgs[language] || confirmMsgs.ru
                        }]);
                      } catch (err) {
                        void 0;
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl 
                               bg-amber-500/10 border border-amber-500/30 text-amber-400
                               hover:bg-amber-500/20 hover:border-amber-500/50
                               transition-all text-sm font-heading tracking-wider"
                    data-testid="accept-askeza-btn"
                  >
                    <Fire size={16} weight="fill" />
                    {t('askeza.accept') || 'Take Askeza'}
                  </button>
                  <button
                    onClick={() => {
                      setAskezaSuggestion(null);
                      handleSendMessage(t('askeza.suggestAnother') || 'Suggest another practice');
                    }}
                    className="px-4 py-2.5 rounded-xl 
                               bg-[var(--overlay-bg)] border border-[var(--glass-border)]
                               text-muted-foreground hover:text-foreground
                               hover:border-starlight/30 transition-all text-sm"
                    data-testid="suggest-another-askeza-btn"
                  >
                    {t('askeza.suggestAnother') || 'Suggest another'}
                  </button>
                </motion.div>
              )}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2 text-muted-foreground"
                >
                  <SpinnerGap size={20} className="animate-spin" />
                  <span className="text-sm italic">{t('chat.oracleThinking')}</span>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </main>

      {/* Input */}
      <div className="relative z-20 bg-[var(--card-bg)]/80 backdrop-blur-xl border-t border-[var(--glass-border)] px-6 py-4">
        {/* Quick Reply Buttons */}
        {getCurrentQuickReplies().length > 0 && !isLoading && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto mb-4"
          >
            <p className="text-xs text-muted-foreground/50 mb-2 text-center">
              {t('chat.selectOrType')}
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {getCurrentQuickReplies().map((reply, idx) => (
                <motion.button
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  onClick={() => sendMessage(null, reply)}
                  className="bg-ethereal/10 border border-ethereal/30 text-foreground/80
                             px-4 py-2 rounded-full text-sm
                             hover:bg-ethereal/20 hover:border-ethereal/50 hover:text-foreground
                             transition-all duration-200 active:scale-95"
                  data-testid={`quick-reply-${idx}`}
                >
                  {reply}
                </motion.button>
              ))}
              {/* Summarize Button - appears after 5 messages or 3 minutes */}
              {shouldShowSummarizeButton() && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  onClick={() => {
                    setProtocolRequested(true);
                    sendMessage(null, t('chat.summarizeText'), true);
                  }}
                  className="bg-starlight/10 border border-starlight/40 text-starlight
                             px-4 py-2 rounded-full text-sm flex items-center gap-2
                             hover:bg-starlight/20 hover:border-starlight/60
                             transition-all duration-200 active:scale-95"
                  data-testid="summarize-btn"
                >
                  <FileText size={16} />
                  {t('chat.summarize')}
                </motion.button>
              )}
            </div>
          </motion.div>
        )}
        
        {/* Auto-sending email indicator */}
        {emailSending && (
          <div className="max-w-3xl mx-auto mb-3 text-center">
            <span className="text-starlight/70 text-xs animate-pulse">{t('chat.sending')}</span>
          </div>
        )}
        {emailSent && (
          <div className="max-w-3xl mx-auto mb-3 text-center">
            <span className="text-green-400/70 text-xs">{t('chat.planSentTo')} {user?.email}</span>
          </div>
        )}

        <form onSubmit={sendMessage} className="max-w-3xl mx-auto flex gap-3 items-center">
          {user && (
            <VoiceRecordButton
              user={user}
              onTranscription={(text) => {
                setInputValue(text);
                // Auto-send after transcription
                setTimeout(() => {
                  sendMessage(null, text);
                }, 100);
              }}
              disabled={isLoading || isStarting}
              language={language}
            />
          )}
          <input
            ref={chatInputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={exchangeCount < 4 ? t('chat.placeholderShort') : t('chat.placeholder')}
            disabled={isLoading || isStarting}
            className="flex-1 bg-[#1a1520] border-2 border-starlight rounded-xl px-5 py-3
                       text-foreground placeholder:text-starlight/50
                       focus:border-starlight focus:outline-none focus:ring-2 focus:ring-starlight/30
                       disabled:opacity-50 transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
            autoFocus
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={isLoading || isStarting || !inputValue.trim()}
            className="bg-starlight/10 border border-starlight/50 text-starlight
                       px-6 py-3 rounded-xl
                       hover:bg-starlight/20 hover:shadow-[0_0_15px_rgba(212,175,55,0.3)]
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-300 active:scale-95"
            data-testid="send-btn"
          >
            <PaperPlaneTilt size={24} weight="duotone" />
          </button>
        </form>
        
        {/* Status Bar */}
        <div className="max-w-3xl mx-auto mt-3">
          {user ? (
            <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground/60">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                {t('chat.statusMode')} {t(`topics.${topic.id}`) || topic.name_ru}
              </span>
              <span className="text-white/10">|</span>
              <span>{t('chat.memoryOn')}</span>
              <span className="text-white/10">|</span>
              <span className="text-starlight/60">{user.total_credits} {t('profile.credits')}</span>
            </div>
          ) : (
            trustScore > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground/50">{t('chat.soulConnection')}</span>
                <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-ethereal to-starlight"
                    initial={{ width: 0 }}
                    animate={{ width: `${trustScore * 100}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                <span className="text-xs text-muted-foreground/50">{Math.round(trustScore * 100)}%</span>
              </div>
            )
          )}
        </div>
      </div>

      {/* Email Modal */}
      <AnimatePresence>
        {showEmailModal && (
          <EmailModal 
            onSubmit={handleEmailSubmit}
            onClose={() => setShowEmailModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Registration Gate Modal — for anonymous users at protocol stage */}
      <AnimatePresence>
        {showRegistrationGate && !user && (
          <RegistrationGateModal
            onShowAuth={onShowAuth}
            onClose={() => setShowRegistrationGate(false)}
          />
        )}
      </AnimatePresence>

      {/* Date Picker for In-Chat Divination */}
      <AnimatePresence>
        {showDatePickerForDiv && (
          <DateInputModal
            title={showDatePickerForDiv === 'horoscope' ? t('chat.divinationHoroscope') : t('chat.divinationNumerology')}
            onSubmit={(data) => {
              const bd = data.birth_date || data.birthDate;
              handleChatDivination(showDatePickerForDiv, bd);
            }}
            onClose={() => setShowDatePickerForDiv(null)}
            isHoroscope={showDatePickerForDiv === 'horoscope'}
            savedProfile={savedProfile}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Main App
// Age Gate — 18+ verification popup
const AGE_VERIFIED_KEY = 'onima_age_verified';
const AgeGate = () => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [verified, setVerified] = useState(() => localStorage.getItem(AGE_VERIFIED_KEY) === 'true');

  if (verified) return null;

  const handleYes = () => {
    localStorage.setItem(AGE_VERIFIED_KEY, 'true');
    setVerified(true);
  };

  const handleNo = () => {
    window.location.href = 'https://www.google.com';
  };

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black/80 backdrop-blur-md px-4" data-testid="age-gate-overlay">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`max-w-sm w-full rounded-2xl p-8 text-center border ${
          isLight
            ? 'bg-white border-gray-200 shadow-2xl'
            : 'bg-[#0d0f1a] border-[var(--glass-border)] shadow-[0_0_60px_rgba(212,175,55,0.1)]'
        }`}
        data-testid="age-gate-modal"
      >
        <div className="flex justify-center mb-4">
          <Eye size={44} weight="duotone" className="text-ethereal" />
        </div>
        <h2 className={`font-heading tracking-[0.2em] uppercase text-lg mb-3 ${isLight ? 'text-gray-900' : 'text-starlight'}`}>
          {t('ageGate.title')}
        </h2>
        <p className={`text-sm mb-6 leading-relaxed ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>
          {t('ageGate.description')}
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleNo}
            className={`flex-1 py-3 rounded-xl text-sm font-heading tracking-wider uppercase transition-all
              ${isLight
                ? 'bg-gray-100 border border-gray-200 text-gray-500 hover:bg-gray-200'
                : 'bg-white/5 border border-white/10 text-muted-foreground hover:bg-white/10'
              }`}
            data-testid="age-gate-no-btn"
          >
            {t('ageGate.no')}
          </button>
          <button
            onClick={handleYes}
            className={`flex-1 py-3 rounded-xl text-sm font-heading tracking-wider uppercase transition-all
              ${isLight
                ? 'bg-amber-500 border border-amber-600 text-white hover:bg-amber-600'
                : 'bg-starlight/15 border border-starlight/40 text-starlight hover:bg-starlight/25 hover:shadow-[0_0_20px_rgba(212,175,55,0.2)]'
              }`}
            data-testid="age-gate-yes-btn"
          >
            {t('ageGate.yes')}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

function App() {
  const { t, language } = useLanguage();
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [pendingTopic, setPendingTopic] = useState(null); // Topic pending confirmation
  const [showOraclePriceModal, setShowOraclePriceModal] = useState(false);
  const [selectedDivination, setSelectedDivination] = useState(null);
  const [divinationResult, setDivinationResult] = useState(null);
  const [showDateModal, setShowDateModal] = useState(false);
  const [isLoadingDivination, setIsLoadingDivination] = useState(false);
  const [showTarotPicker, setShowTarotPicker] = useState(false);
  const [tarotApiPromise, setTarotApiPromise] = useState(null);
  const [tarotInputData, setTarotInputData] = useState(null);
  const [routedSession, setRoutedSession] = useState(null); // session from inline chat
  const [showAskezaChat, setShowAskezaChat] = useState(false); // Dedicated askeza AI chat
  const [clarifyMode, setClarifyMode] = useState(false); // For clarifying questions
  const [previousQuestion, setPreviousQuestion] = useState(null); // Store previous question for context
  const [freeReadingsUsed, setFreeReadingsUsed] = useState(0); // Track free readings for non-registered users
  const [showLimitModal, setShowLimitModal] = useState(false); // Show when limit reached
  
  const FREE_READINGS_LIMIT = 1; // Number of free readings before registration required
  
  // Auth state
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCreditShop, setShowCreditShop] = useState(false);
  const [showLowCreditsWarning, setShowLowCreditsWarning] = useState(true);
  const [googleAuthLoading, setGoogleAuthLoading] = useState(false);
  const [savedProfile, setSavedProfile] = useState(null);

  // Load free readings count from localStorage on mount
  useEffect(() => {
    const storedCount = localStorage.getItem('onima_free_readings');
    if (storedCount) {
      setFreeReadingsUsed(parseInt(storedCount, 10));
    }
  }, []);

  // Load divination profile when user is available
  useEffect(() => {
    if (user?.id) {
      axios.get(`${API}/user/divination-profile/${user.id}`)
        .then(res => setSavedProfile(res.data.profile))
        .catch(() => {});
    } else {
      setSavedProfile(null);
    }
  }, [user?.id]);

  // Load user on mount + handle Google Auth callback
  useEffect(() => {
    // FIRST: Always check for referral code in URL (before anything else)
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      sessionStorage.setItem('referral_code', refCode);
      void 0;
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    const handleGoogleCallback = async () => {
      // Google Auth returns: {redirect_url}#session_id={session_id}
      let sessionId = null;
      
      // Primary: check hash fragment (Emergent Auth format)
      if (window.location.hash?.includes('session_id=')) {
        const hashStr = window.location.hash.substring(1); // remove #
        const hashParams = new URLSearchParams(hashStr);
        sessionId = hashParams.get('session_id');
      }
      
      // Fallback: check query params
      if (!sessionId) {
        const params = new URLSearchParams(window.location.search);
        sessionId = params.get('session_id');
      }

      if (sessionId) {
        setGoogleAuthLoading(true);
        try {
          const res = await axios.post(`${API}/auth/google/session`, { 
            session_id: sessionId,
            referral_code: sessionStorage.getItem('referral_code') || undefined
          });
          if (res.data.success) {
            saveUser(res.data.user, res.data.token);
            setUser(res.data.user);
          }
        } catch (e) {
          void 0;
        } finally {
          setGoogleAuthLoading(false);
        }
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      const stored = loadUser();
      if (stored?.user) {
        axios.get(`${API}/auth/me/${stored.token}`)
          .then(res => setUser(res.data))
          .catch(() => {
            clearUser();
            setUser(null);
          });
      }
    };
    handleGoogleCallback();
  }, []);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setShowAuthModal(false);
  };

  const handleUserUpdate = (userData) => {
    setUser(userData);
    saveUser(userData, loadUser()?.token);
  };

  const handleLogout = () => {
    clearUser();
    setUser(null);
  };

  const [showHoroscopeSelection, setShowHoroscopeSelection] = useState(false);
  const [selectedIndividualHoroscope, setSelectedIndividualHoroscope] = useState(null);
  const [horoscopeOrderSuccess, setHoroscopeOrderSuccess] = useState(null);

  const handleSelectDivination = (method) => {
    // Check if non-registered user has reached free limit
    if (!user && freeReadingsUsed >= FREE_READINGS_LIMIT) {
      setShowLimitModal(true);
      return;
    }
    
    trackEvent('divination_selected', user?.id, { method: method.id });

    // For horoscope, show selection modal first
    if (method.id === 'horoscope') {
      setShowHoroscopeSelection(true);
      return;
    }

    setSelectedDivination(method);
    setShowDateModal(true);
  };

  const handleIndividualHoroscopeOrder = async (horoscopeType, formData) => {
    try {
      const response = await axios.post(`${API}/horoscopes/order`, {
        user_id: user.id,
        horoscope_type: horoscopeType.id,
        user_data: formData,
        language: language
      });
      if (response.data.user) handleUserUpdate(response.data.user);
      setSelectedIndividualHoroscope(null);
      setShowHoroscopeSelection(false);
      setHoroscopeOrderSuccess(response.data);
    } catch (e) {
      throw e;
    }
  };

  const performDivination = async (methodId, inputData) => {
    // For tarot: show card picker with parallel API call
    if (methodId === 'tarot') {
      setShowDateModal(false);
      // Increment free readings for non-registered users
      if (!user) {
        const newCount = freeReadingsUsed + 1;
        setFreeReadingsUsed(newCount);
        localStorage.setItem('onima_free_readings', newCount.toString());
      }
      // Start API call in background, store the promise
      const userId = user?.id || null;
      const promise = axios.post(`${API}/divination/tarot`, {
        user_id: userId,
        topic: inputData?.topic || null,
        language: language
      });
      setTarotApiPromise(promise);
      setTarotInputData(inputData);
      setShowTarotPicker(true);
      return;
    }

    setIsLoadingDivination(true);
    setShowDateModal(false);
    
    // Increment free readings count for non-registered users
    if (!user) {
      const newCount = freeReadingsUsed + 1;
      setFreeReadingsUsed(newCount);
      localStorage.setItem('onima_free_readings', newCount.toString());
    }
    
    try {
      let response;
      const userId = user?.id || null;
      if (methodId === 'horoscope') {
        const isExtended = typeof inputData === 'object';
        const payload = isExtended ? {
          ...inputData,
          period: t('divination.today'),
          user_id: userId,
          user_date: new Date().toISOString().split('T')[0]
        } : {
          birth_date: inputData,
          period: t('divination.today'),
          user_id: userId,
          user_date: new Date().toISOString().split('T')[0]
        };
        response = await axios.post(`${API}/divination/horoscope`, payload);
      } else if (methodId === 'numerology') {
        const isObject = typeof inputData === 'object';
        response = await axios.post(`${API}/divination/numerology`, {
          birth_date: isObject ? inputData.birth_date : inputData,
          topic: isObject ? inputData.topic : null,
          user_id: userId
        });
      }
      if (response.data.user) {
        handleUserUpdate(response.data.user);
      }
      setDivinationResult(response.data);
      setClarifyMode(false);
      setPreviousQuestion(null);
    } catch (e) {
      void 0;
      if (e.response?.status === 402) {
        alert(t('app.insufficientCredits'));
        setIsLoadingDivination(false);
        return;
      }
    } finally {
      setIsLoadingDivination(false);
    }
  };

  // Handle tarot picker completion
  const handleTarotPickerComplete = (result, error) => {
    setShowTarotPicker(false);
    setTarotApiPromise(null);
    setTarotInputData(null);
    if (error?.cancelled) {
      // User cancelled
      return;
    }
    if (result) {
      if (result.user) {
        handleUserUpdate(result.user);
      }
      setDivinationResult(result);
      setClarifyMode(false);
      setPreviousQuestion(null);
    } else if (error) {
      void 0;
      if (error.response?.status === 402) {
        alert(t('app.insufficientCredits'));
      }
    }
  };

  const handleDateSubmit = (birthData) => {
    if (selectedDivination) {
      performDivination(selectedDivination.id, birthData);
    }
  };

  // Handle new reading from result page
  const handleNewReading = (currentType) => {
    setDivinationResult(null);
    setClarifyMode(false);
    setPreviousQuestion(null);
    // Open the question modal for the same type
    const methods = [
      { id: 'tarot' },
      { id: 'horoscope' },
      { id: 'numerology' }
    ];
    const method = methods.find(m => m.id === currentType);
    if (method) {
      setSelectedDivination(method);
      setShowDateModal(true);
    }
  };

  // Handle clarifying question from result page
  const handleClarifyQuestion = (previousResult) => {
    setDivinationResult(null);
    setClarifyMode(true);
    // Store context for clarifying
    setPreviousQuestion({
      type: previousResult.type,
      topic: previousResult.topic || '',
      interpretation: previousResult.interpretation?.substring(0, 200) || ''
    });
    // Open the question modal
    setSelectedDivination({ id: previousResult.type });
    setShowDateModal(true);
  };

  const resetAll = () => {
    setSelectedTopic(null);
    setSelectedDivination(null);
    setDivinationResult(null);
    setShowDateModal(false);
    setShowProfile(false);
    setClarifyMode(false);
    setPreviousQuestion(null);
  };

  // Google Auth loading
  if (googleAuthLoading) {
    return (
      <div className="App dark min-h-screen bg-background flex items-center justify-center">
        <AnimatedBackground />
        <div className="text-center relative z-10">
          <SpinnerGap size={64} className="text-starlight animate-spin mx-auto mb-4" />
          <p className="font-heading text-starlight tracking-widest uppercase">
            {t('app.googleLoading')}
          </p>
        </div>
      </div>
    );
  }

  // Loading state for divination
  if (isLoadingDivination) {
    return (
      <div className="App dark min-h-screen bg-background flex items-center justify-center">
        <AnimatedBackground />
        <div className="text-center relative z-10">
          <SpinnerGap size={64} className="text-secondary animate-spin mx-auto mb-4" />
          <p className="font-heading text-secondary tracking-widest uppercase">
            {t('app.divinationLoading')}
          </p>
        </div>
      </div>
    );
  }

  // Show divination result
  if (divinationResult) {
    return (
      <div className="App dark">
        <UserHeader user={user} onLogout={handleLogout} onShowAuth={() => setShowAuthModal(true)} onShowProfile={() => setShowProfile(true)} onShowCreditShop={() => setShowCreditShop(true)} />
        <DivinationResult 
          result={divinationResult} 
          onBack={resetAll} 
          user={user}
          onNewReading={handleNewReading}
          onClarifyQuestion={handleClarifyQuestion}
          onShowAuth={() => setShowAuthModal(true)}
        />
        <AnimatePresence>
          {showAuthModal && (
            <AuthModal onClose={() => setShowAuthModal(false)} onSuccess={handleAuthSuccess} />
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Show profile page
  if (showProfile && user) {
    return (
      <div className="App dark">
        <ProfilePage
          user={user}
          onBack={() => setShowProfile(false)}
          onLogout={handleLogout}
          onUserUpdate={handleUserUpdate}
        />
      </div>
    );
  }

  return (
    <div className="App dark">
      <AgeGate />
      <BrowserRouter>
        <Routes>
          <Route path="/admin" element={<AdminPanel />} />
          {/* Guide/SEO pages */}
          <Route path="/guide/:lang/:category/:slug" element={<GuidePage />} />
          {/* Legal pages */}
          <Route path="/disclaimer" element={<LegalPage type="disclaimer" />} />
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          <Route path="/terms" element={<LegalPage type="terms" />} />
          <Route path="/contacts" element={<LegalPage type="contacts" />} />
          <Route path="/pricing" element={<LegalPage type="pricing" />} />
          <Route path="/impressum" element={<LegalPage type="impressum" />} />
          <Route path="/datenschutz" element={<LegalPage type="datenschutz" />} />
          <Route path="*" element={
            <div className={`flex flex-col ${(showAskezaChat || selectedTopic) ? 'h-screen' : 'min-h-screen'}`}>
              <UserHeader user={user} onLogout={handleLogout} onShowAuth={() => setShowAuthModal(true)} onShowProfile={() => setShowProfile(true)} onShowCreditShop={() => setShowCreditShop(true)} />
              <div className="flex-1 flex flex-col overflow-hidden">
                {showAskezaChat ? (
                  <AskezaChat
                    user={user}
                    onBack={() => setShowAskezaChat(false)}
                    onUserUpdate={handleUserUpdate}
                    onShowAuth={() => setShowAuthModal(true)}
                  />
                ) : selectedTopic ? (
                  <ChatPage 
                    topic={selectedTopic} 
                    onBack={() => { setSelectedTopic(null); setRoutedSession(null); }}
                    user={user}
                    onUserUpdate={handleUserUpdate}
                    savedProfile={savedProfile}
                    onShowAuth={() => setShowAuthModal(true)}
                    routedSession={routedSession}
                  />
                ) : (
                  <Home 
                    onSelectTopic={(topic) => {
                      trackEvent('topic_selected', user?.id, { topic: topic.id });
                      setPendingTopic(topic);
                      setShowOraclePriceModal(true);
                    }}
                    onSelectDivination={handleSelectDivination}
                    onStartInlineChat={(data) => {
                      trackEvent('inline_chat_started', user?.id, { topic: data.topicId });
                      if (data.user) handleUserUpdate(data.user);
                      setRoutedSession(data);
                      setSelectedTopic({ id: data.topicId, ...(data.topic || {}) });
                    }}
                    onStartAskezaChat={() => {
                      trackEvent('askeza_chat_started', user?.id);
                      setShowAskezaChat(true);
                    }}
                    user={user}
                    language={language}
                  />
                )}
              </div>
              {!showAskezaChat && !selectedTopic && <Footer />}
            </div>
          } />
        </Routes>
        {/* Cookie Consent Banner */}
        <CookieConsent />
      </BrowserRouter>

      {/* Date Input Modal / Tarot Question Modal */}
      <AnimatePresence>
        {showDateModal && selectedDivination?.id === 'tarot' && (
          <TarotQuestionModal
            title={t('divination.tarotTitle') || 'Расклад Таро'}
            onSubmit={handleDateSubmit}
            onClose={() => {
              setShowDateModal(false);
              setSelectedDivination(null);
            }}
          />
        )}
        {showDateModal && selectedDivination?.id !== 'tarot' && (
          <DateInputModal
            title={selectedDivination ? t(`divination.${selectedDivination.id}Title`) : ''}
            onSubmit={handleDateSubmit}
            onClose={() => {
              setShowDateModal(false);
              setSelectedDivination(null);
            }}
            isHoroscope={selectedDivination?.id === 'horoscope'}
            isTarot={false}
            savedProfile={savedProfile}
            user={user}
          />
        )}
      </AnimatePresence>

      {/* Tarot Card Picker Overlay */}
      <AnimatePresence>
        {showTarotPicker && (
          <TarotCardPicker
            apiPromise={tarotApiPromise}
            onComplete={handleTarotPickerComplete}
          />
        )}
      </AnimatePresence>

      {/* Horoscope Selection Modal */}
      <AnimatePresence>
        {showHoroscopeSelection && !selectedIndividualHoroscope && (
          <HoroscopeSelectionModal
            user={user}
            onSelectStandard={() => {
              setShowHoroscopeSelection(false);
              setSelectedDivination({ id: 'horoscope' });
              setShowDateModal(true);
            }}
            onSelectIndividual={(htype) => {
              setSelectedIndividualHoroscope(htype);
            }}
            onClose={() => setShowHoroscopeSelection(false)}
          />
        )}
        {selectedIndividualHoroscope && (
          <IndividualHoroscopeForm
            horoscopeType={selectedIndividualHoroscope}
            savedProfile={savedProfile}
            onSubmit={(formData) => handleIndividualHoroscopeOrder(selectedIndividualHoroscope, formData)}
            onBack={() => setSelectedIndividualHoroscope(null)}
            onClose={() => { setSelectedIndividualHoroscope(null); setShowHoroscopeSelection(false); }}
          />
        )}
        {horoscopeOrderSuccess && (
          <HoroscopeOrderSuccess
            orderData={horoscopeOrderSuccess}
            onClose={() => setHoroscopeOrderSuccess(null)}
          />
        )}
      </AnimatePresence>
      
      {/* Oracle Price Confirmation Modal */}
      <AnimatePresence>
        {showOraclePriceModal && pendingTopic && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => {
              setShowOraclePriceModal(false);
              setPendingTopic(null);
            }} />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-[var(--color-card)] backdrop-blur-xl border border-secondary/30 
                         rounded-2xl p-8 max-w-sm w-full text-center"
            >
              {/* Topic icon */}
              <div className="w-16 h-16 rounded-full bg-secondary/10 border border-secondary/30 
                              flex items-center justify-center mx-auto mb-4">
                {pendingTopic.id === 'money' && <Coin size={32} className="text-secondary" />}
                {pendingTopic.id === 'love' && <Heart size={32} className="text-secondary" />}
                {pendingTopic.id === 'asceticism' && <Fire size={32} className="text-secondary" />}
                {pendingTopic.id === 'crisis' && <CloudLightning size={32} className="text-secondary" />}
                {pendingTopic.id === 'business' && <Briefcase size={32} className="text-secondary" />}
                {pendingTopic.id === 'growth' && <Plant size={32} className="text-secondary" />}
              </div>
              
              <h3 className="font-heading text-xl text-secondary tracking-widest uppercase mb-2">
                {t(`topics.${pendingTopic.id}`)}
              </h3>
              <p className="text-muted-foreground text-sm mb-6">
                {t(`topics.${pendingTopic.id}Desc`)}
              </p>
              
              {/* Price info */}
              <div className="bg-secondary/5 border border-secondary/20 rounded-xl p-4 mb-6">
                <p className="text-foreground/80 text-sm">
                  {t('oracle.priceInfo') || 'Стоимость диалога с Оракулом:'}
                </p>
                <p className="text-secondary font-heading text-lg mt-1 flex items-center justify-center gap-2">
                  1 <MarioCoin size={20} /> {t('oracle.perResponse') || 'за ответ'}
                </p>
              </div>
              
              <button
                onClick={() => {
                  setSelectedTopic(pendingTopic);
                  setShowOraclePriceModal(false);
                  setPendingTopic(null);
                }}
                className="w-full bg-secondary/10 border border-secondary/50 text-secondary
                           py-4 rounded-xl font-heading tracking-widest uppercase
                           hover:bg-secondary/20 transition-all active:scale-[0.98]"
                data-testid="oracle-start-btn"
              >
                {t('oracle.startConversation') || 'Начать диалог'}
              </button>
              
              <button
                onClick={() => {
                  setShowOraclePriceModal(false);
                  setPendingTopic(null);
                }}
                className="w-full text-muted-foreground text-sm mt-4 hover:text-foreground transition-colors"
              >
                {t('chat.back')}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Free Readings Limit Modal */}
      <AnimatePresence>
        {showLimitModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowLimitModal(false)} />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-[var(--color-card)] backdrop-blur-xl border border-starlight/30 
                         rounded-2xl p-8 max-w-sm w-full text-center"
            >
              {/* Icon */}
              <div className="w-16 h-16 rounded-full bg-starlight/10 border border-starlight/30 
                              flex items-center justify-center mx-auto mb-4">
                <MoonStars size={32} className="text-starlight" />
              </div>
              
              <h3 className="font-heading text-xl text-starlight tracking-widest uppercase mb-2">
                {t('limit.title') || 'Бесплатное гадание использовано'}
              </h3>
              <p className="text-muted-foreground text-sm mb-6">
                {t('limit.description') || 'Вы уже получили своё бесплатное гадание. Зарегистрируйтесь, чтобы продолжить исследовать тайны судьбы.'}
              </p>
              
              {/* Bonus highlight */}
              <div className="bg-starlight/10 border border-starlight/30 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-starlight">
                  <Gift size={20} />
                  <span className="font-heading tracking-wider">
                    {t('auth.freeCreditsBonus') || '100 бесплатных кредитов'}
                  </span>
                </div>
                <p className="text-starlight/60 text-xs mt-1">
                  {t('auth.creditsFor') || '= 10 гаданий бесплатно'}
                </p>
              </div>
              
              <button
                onClick={() => {
                  setShowLimitModal(false);
                  setShowAuthModal(true);
                }}
                className="w-full bg-starlight/10 border border-starlight/50 text-starlight
                           py-4 rounded-xl font-heading tracking-widest uppercase
                           hover:bg-starlight/20 transition-all active:scale-[0.98] mb-3"
                data-testid="limit-register-btn"
              >
                {t('auth.registerFree') || 'Регистрация бесплатно'}
              </button>
              
              <p className="text-muted-foreground text-sm">
                {t('auth.alreadyHaveAccount') || 'Уже есть аккаунт?'}{' '}
                <button 
                  onClick={() => {
                    setShowLimitModal(false);
                    setShowAuthModal(true);
                  }}
                  className="text-starlight hover:underline"
                >
                  {t('auth.loginButton') || 'Войти'}
                </button>
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Credit Shop Modal */}
      <AnimatePresence>
        {showCreditShop && (
          <CreditShopModal
            user={user}
            onClose={() => { setShowCreditShop(false); setShowLowCreditsWarning(true); }}
          />
        )}
      </AnimatePresence>

      {/* Low Credits Warning — floats above chat input bar */}
      {user && user.total_credits < 25 && user.total_credits > 0 && !showProfile && !showCreditShop && showLowCreditsWarning && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 animate-in fade-in slide-in-from-bottom-4">
          <div className="bg-amber-900/90 backdrop-blur-xl border border-amber-500/30 rounded-full px-4 py-2
                         flex items-center gap-2 shadow-lg">
            <button
              onClick={() => setShowCreditShop(true)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              data-testid="low-credits-warning"
            >
              <MarioCoin size={16} />
              <span className="text-amber-200 text-sm">{t('credits.lowBalance') || 'Кредиты заканчиваются'}</span>
              <span className="text-amber-400 font-mono text-sm font-bold">{user.total_credits}</span>
            </button>
            <button
              onClick={() => setShowLowCreditsWarning(false)}
              className="text-amber-500/60 hover:text-amber-300 transition-colors ml-1 leading-none"
              aria-label="Dismiss"
              data-testid="dismiss-low-credits"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AnimatePresence>
        {showAuthModal && (
          <AuthModal onClose={() => setShowAuthModal(false)} onSuccess={handleAuthSuccess} />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
